import React, {
  Fragment,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageNavbar } from '@/pages/account';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { KeenIcon } from '@/components';
import { Editor } from '@tinymce/tinymce-react';
import {
  harborValidationSchema,
  isValidCanonicalPhone,
} from '@/utils/validations';
import {
  useCreateHarborMutation,
  useUpdateHarborMutation,
  useCreateMediaHarborIdMutation,
  useUpdateFeaturedHarborMutation,
  useGetCoastalAreaQuery,
  useTranslateHarborContentMutation,
  useGetHarborVatRegimesQuery,
  useGetHarborVatRegimeByIdQuery,
} from '@/services/HarborManagementService';
import { ErrorApiResponse, RawImageCaption } from '@/types/api.types';
import {
  CoastalAreaResponse,
  IHarborData,
  MediaPayload,
  HarborStatusType,
  HarborVatRegime,
  SeasonDateRange,
} from '@/types/harbor-management.types';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';
import clsx from 'clsx';
import {
  getDialCodeFromCountryShortCode,
  normalizeDialCode,
  normalizeNationalPhoneNumber,
  resolvePhoneCountryShortCode,
} from '@/utils/phone';
import {
  textFieldValidation,
  imageValidation,
  businessHoursValidation,
  mediaValidation,
} from '@/utils/validations';
import BusinessHours from './BusinessHours';
import MediaUpload from './MediaUpload';
import FacilityConditionManager from './FacilityConditionManager';
import CountryCodeSelect from '@/components/CountryCodeSelect/CountryCodeSelect';
import countries from 'world-countries';
import { localizedCountryOptions } from '@/utils/countryName';
import { useGetUsersQuery } from '@/services/UserManagementService';
import { PaginatedApiResponse } from '@/types/api.types';
import { UserResponse } from '@/types/user-management.types';
import useDebounce from '@/hooks/useDebounce';
import {
  Statuses,
  UserRoles,
  HarborStatus,
  harborTypeOptions,
  HarborBookingType,
  TIMEZONE_OPTIONS,
  tinymceInit,
  TINYMCE_SCRIPT_SRC,
} from '@/constant';
import {
  formatManagedListKey,
  useManagedListOptions,
} from '@/hooks/useManagedListOptions';
import { ManagedListType } from '@/types/list-value.types';
import { Tooltip } from 'react-tooltip';
import Select, { MultiValue } from 'react-select';
import { useGetRegionsQuery } from '@/services/RegionManagementService';
import { useGetYachtsQuery } from '@/services/YatchManagementService';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import { useIntl } from 'react-intl';
import HarborPreviewModal from './HarborPreviewModal';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { HarborCapability } from '@/constant/harbour';
import { isProductVatConfigActive } from '@/pages/product-vat-config/productVatConfigUtils';
import NumberInput from '@/components/inputControls/NumberInput';
import { formatDateWithOptions } from '@/utils/Date';
import { formatFinnishPercentage } from '@/utils/Currency';
import PriceInput from '@/components/inputControls/PriceInput';

interface MarinaOption {
  id: string;
  name: string;
  email?: string;
}

export interface RegionOption {
  value: string;
  label: string;
}

interface YachtClubOption {
  id: string;
  name: string;
  label: string;
  value: string;
}

const getDefaultTimezoneForCountry = (countryShortCode?: string): string => {
  if (!countryShortCode) return '';
  switch (countryShortCode) {
    case 'FI':
      return 'Europe/Helsinki';
    case 'DE':
      return 'Europe/Berlin';
    case 'PL':
      return 'Europe/Warsaw';
    case 'DK':
      return 'Europe/Copenhagen';
    default:
      return '';
  }
};

// Local type to represent facility conditions coming from API which may include
// availability fields not present in the base type definition
interface FacilityConditionFromApi {
  facility_name: string;
  condition: string;
  updated_at?: string;
  updated_by?: { id: string; name?: string; email?: string };
  availability_status?: 'always' | 'seasonal' | 'closed';
  seasonal_availability?: {
    spring?: SeasonDateRange[];
    summer?: SeasonDateRange[];
    autumn?: SeasonDateRange[];
    winter?: SeasonDateRange[];
  };
}

interface HarborFormProps {
  mode?: 'create' | 'edit';
}

interface YachtResponse {
  id: string;
  name: string;
  description: string;
  logo: string | null;
  location: string;
  website: string;
  contact_email: string;
  status: number;
  harbors: Array<{
    id: string;
    name: string;
    location: string;
  }>;
  yachtClubAdmin: {
    id: string;
    name: string;
    email: string;
    profile_picture: string | null;
  };
  created_at: string;
  updated_at: string;
}

// Language options will be generated dynamically using intl

const HarborForm: React.FC<HarborFormProps> = ({ mode = 'create' }) => {
  const CURRENT_YEAR = new Date().getFullYear().toString();
  const [translateHarborContent] = useTranslateHarborContentMutation();
  const navigate = useNavigate();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const harborData = (location.state as { harbor?: IHarborData; mode?: string })
    ?.harbor;
  const isViewMode =
    (location.state as { mode?: string } | null)?.mode === 'view';
  const { currentLanguage, changeLanguage } = useLanguage();
  const { hasCapability } = usePlanPermissions();

  const getHarborStatusLabel = (status: number | string | undefined) => {
    const numericStatus = Number(status);
    switch (numericStatus) {
      case HarborStatusType.ACTIVE:
        return intl.formatMessage({ id: 'COMMON.ACTIVE' });
      case HarborStatusType.INACTIVE:
        return intl.formatMessage({ id: 'COMMON.INACTIVE' });
      case HarborStatusType.PENDING_DELETION:
        return 'Pending Deletion';
      case HarborStatusType.DELETED:
        return 'Deleted';
      case HarborStatusType.UNDER_RENOVATION:
        return intl.formatMessage({ id: 'COMMON.UNDER_RENOVATION' });
      default:
        return 'Unknown';
    }
  };

  // Generate language options with translations
  const LANGUAGE_OPTIONS = [
    { value: 'English', label: intl.formatMessage({ id: 'LANGUAGE.ENGLISH' }) },
    { value: 'German', label: intl.formatMessage({ id: 'LANGUAGE.GERMAN' }) },
    { value: 'Finnish', label: intl.formatMessage({ id: 'LANGUAGE.FINNISH' }) },
    { value: 'Swedish', label: intl.formatMessage({ id: 'LANGUAGE.SWEDISH' }) },
  ];

  // Helper functions for managing seasonal date ranges
  const addDateRange = (season: 'spring' | 'summer' | 'autumn' | 'winter') => {
    setFormData((prev) => {
      const currentRanges = prev.seasonal_dates?.[season] || [];
      return {
        ...prev,
        seasonal_dates: {
          ...prev.seasonal_dates,
          [season]: [
            ...currentRanges,
            {
              start_date: '',
              end_date: '',
              is_closed: false,
            },
          ],
        },
      };
    });
  };

  const removeDateRange = (
    season: 'spring' | 'summer' | 'autumn' | 'winter',
    index: number
  ) => {
    setFormData((prev) => {
      const currentRanges = prev.seasonal_dates?.[season] || [];
      const newRanges = currentRanges.filter((_, i) => i !== index);

      // If this was the last range, add a new empty one
      const updatedRanges =
        newRanges.length === 0
          ? [
              {
                start_date: '',
                end_date: '',
                is_closed: false,
              },
            ]
          : newRanges;

      return {
        ...prev,
        seasonal_dates: {
          ...prev.seasonal_dates,
          [season]: updatedRanges,
        },
      };
    });
  };

  const detectSeasonalDateOverlaps = useCallback(
    (seasonalDates: Partial<IHarborData>['seasonal_dates']) => {
      const baseYear = new Date().getFullYear();
      if (!seasonalDates) return { hasOverlap: false, seasons: [] as string[] };

      const parseMonthDay = (
        isoDate: string
      ): { month: number; day: number } | null => {
        const trimmed = (isoDate || '').trim();
        if (!trimmed) return null;

        const parts = trimmed.split('-');
        if (parts.length < 2) return null;

        const monthPart = parts.length === 3 ? parts[1] : parts[0];
        const dayPart = parts.length === 3 ? parts[2] : parts[1];

        const month = Number(monthPart);
        const day = Number(dayPart);
        if (!Number.isFinite(month) || !Number.isFinite(day)) return null;
        if (month < 1 || month > 12) return null;
        if (day < 1 || day > 31) return null;

        return { month, day };
      };

      const resolveRangeToDates = (range: SeasonDateRange) => {
        const start =
          typeof range.start_date === 'string' ? range.start_date : '';
        const end = typeof range.end_date === 'string' ? range.end_date : '';

        const startParts = start.split('-');
        const endParts = end.split('-');

        const hasFullYearStart =
          startParts.length === 3 && startParts[0].length === 4;
        const hasFullYearEnd =
          endParts.length === 3 && endParts[0].length === 4;

        const toDate = (y: number, m: number, d: number) =>
          new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));

        // If we already have full YYYY-MM-DD in both, use them directly
        if (hasFullYearStart && hasFullYearEnd) {
          const s = new Date(`${start}T00:00:00Z`);
          const e = new Date(`${end}T00:00:00Z`);
          if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()))
            return null;
          return { start: s, end: e };
        }

        const sMD = parseMonthDay(start);
        const eMD = parseMonthDay(end);
        if (!sMD || !eMD) return null;

        // Cross-year: if start month is after end month (existing logic)
        const isCrossYear = sMD.month > eMD.month;
        const sDate = toDate(baseYear, sMD.month, sMD.day);
        const eDate = toDate(
          isCrossYear ? baseYear + 1 : baseYear,
          eMD.month,
          eMD.day
        );
        return { start: sDate, end: eDate };
      };

      const seasons = ['spring', 'summer', 'autumn', 'winter'] as const;
      const intervals: Array<{ season: string; start: Date; end: Date }> = [];

      seasons.forEach((season) => {
        const ranges = seasonalDates[season] || [];
        (ranges || []).forEach((r) => {
          const resolved = resolveRangeToDates(r as SeasonDateRange);
          if (!resolved) return;

          if (resolved.end.getTime() <= resolved.start.getTime()) return;
          intervals.push({ season, start: resolved.start, end: resolved.end });
        });
      });

      for (let i = 0; i < intervals.length; i++) {
        for (let j = i + 1; j < intervals.length; j++) {
          const a = intervals[i];
          const b = intervals[j];
          const intersects =
            a.start.getTime() <= b.end.getTime() &&
            b.start.getTime() <= a.end.getTime();
          if (intersects) {
            return {
              hasOverlap: true,
              seasons: Array.from(new Set([a.season, b.season])),
            };
          }
        }
      }

      return { hasOverlap: false, seasons: [] as string[] };
    },
    []
  );

  const updateDateRange = (
    season: 'spring' | 'summer' | 'autumn' | 'winter',
    index: number,
    field: 'start_date' | 'end_date' | 'is_closed',
    value: string | boolean
  ) => {
    setFormData((prev) => {
      const currentRanges = prev.seasonal_dates?.[season] || [];
      const updatedRanges = [...currentRanges];

      // Ensure the range at the specified index exists
      if (!updatedRanges[index]) {
        updatedRanges[index] = {
          start_date: '',
          end_date: '',
          is_closed: false,
        };
      }

      // Update the specified field
      updatedRanges[index] = {
        ...updatedRanges[index],
        [field]: value,
      };

      const nextSeasonalDates = {
        ...prev.seasonal_dates,
        [season]: updatedRanges,
      };

      const overlap = detectSeasonalDateOverlaps(nextSeasonalDates);
      if (overlap.hasOverlap) {
        setErrors((prevErrors) => {
          const msg = intl.formatMessage({
            id: 'HARBOR.VALIDATION.DATE_RANGES_OVERLAP',
            defaultMessage: 'Date ranges cannot overlap',
          });

          const overlapLabel = intl.formatMessage({
            id: 'COMMON.OVERLAP',
            defaultMessage: 'Overlap',
          });

          const seasonalDateLines = overlap.seasons.map((s) => {
            const seasonName = intl.formatMessage({
              id: `HARBOR.SEASON.${String(s).toUpperCase()}`,
              defaultMessage: String(s),
            });
            return `${seasonName} - ${overlapLabel}: ${msg}`;
          });

          return {
            ...prevErrors,
            seasonal_dates: seasonalDateLines,
          };
        });
        return prev;
      }

      setErrors((prevErrors) => {
        if (!prevErrors.seasonal_dates) return prevErrors;
        const nextErrors = { ...prevErrors };
        delete nextErrors.seasonal_dates;
        return nextErrors;
      });

      return {
        ...prev,
        seasonal_dates: {
          ...nextSeasonalDates,
        },
      };
    });
  };

  const [formData, setFormData] = useState<
    Partial<IHarborData> & { harbor_type?: string }
  >(() => {
    const initialData: Partial<IHarborData> = {
      name: '',
      description: '',
      location: '',
      city: '',
      latitude: undefined,
      longitude: undefined,
      vhf_channel: undefined,
      contact_email: '',
      contact_phone: '',
      country_code: '+358',
      country_short_code: 'FI',
      contact_phone_country_short_code: 'FI',
      product_vat_config_id: null,
      default_check_in_time: '15:00',
      default_check_out_time: '12:00',
      check_in_window_start: '',
      check_in_window_end: '',
      check_out_window_start: '',
      check_out_window_end: '',
      timezone: getDefaultTimezoneForCountry('FI'),
      website_url: '',
      berth_capacity: undefined,
      admin_email: '',
      min_depth: undefined,
      max_depth: undefined,
      depth_unit: 'm',
      facilities: [],
      facilities_with_conditions: [],
      amenities: [],
      business_hours: JSON.stringify({
        monday: {},
        tuesday: {},
        wednesday: {},
        thursday: {},
        friday: {},
        saturday: {},
        sunday: {},
      }),
      status: Statuses.ACTIVE,
      logo: null,
      fairway_map: [],
      fairway_entrance_map: [],
      berth_image: null,
      media: [],
      marina: { id: '', name: '' },
      marina_id: '',
      harbor_type: '',
      category: '',
      regionIds: [],
      clubId: '',
      send_booking_to_admin: true,
      mediation_response_deadline_hours: 48,
      booking_enabled: false,
      booking_type: HarborBookingType.RESERVATION,
      whatsapp_link: '',
      facebook_link: '',
      instagram_link: '',
      pdf_link: '',
      terms_pdf: null,
      admin_notes: '',
      primary_language_spoken: '',
      coastal_area: '',
      serviceIds: [],
      seasonality_support: false,
      seasonal_dates: {
        spring: [
          {
            start_date: '',
            end_date: '',
            is_closed: false,
          },
        ],
        summer: [
          {
            start_date: '',
            end_date: '',
            is_closed: false,
          },
        ],
        autumn: [
          {
            start_date: '',
            end_date: '',
            is_closed: false,
          },
        ],
        winter: [
          {
            start_date: '',
            end_date: '',
            is_closed: false,
          },
        ],
      } as const,
    };

    return initialData;
  });
  const { options: harborCategoryOptions, keys: harborCategoryKeys } =
    useManagedListOptions(ManagedListType.HARBOR_CATEGORY);
  const resolvedHarborCategoryOptions = useMemo(() => {
    const options = [...harborCategoryOptions];
    const currentCategory = String(formData.category || '').trim();

    if (
      currentCategory &&
      !options.some((option) => option.value === currentCategory)
    ) {
      options.push({
        id: currentCategory,
        value: currentCategory,
        label: formatManagedListKey(currentCategory),
      });
    }

    return options;
  }, [formData.category, harborCategoryOptions]);

  const isStatusEditable =
    !isViewMode &&
    ![HarborStatusType.PENDING_DELETION, HarborStatusType.DELETED].includes(
      Number(formData.status)
    );

  useEffect(() => {
    if (harborData && mode === 'edit') {
      const resolvedCountryShort = harborData.country_short_code || 'FI';
      const resolvedPhoneCountryShort = resolvePhoneCountryShortCode(
        harborData.country_code,
        harborData.contact_phone_country_short_code,
        resolvedCountryShort
      );
      const resolvedPhoneCountryCode =
        normalizeDialCode(harborData.country_code) ||
        getDialCodeFromCountryShortCode(resolvedPhoneCountryShort);
      const rawTimezone = (harborData.timezone || '').trim();
      const isValidTimezone = TIMEZONE_OPTIONS.some(
        (opt) => opt.value === rawTimezone
      );
      const effectiveTimezone = isValidTimezone
        ? rawTimezone
        : getDefaultTimezoneForCountry(resolvedCountryShort);

      // Seed translations from API and determine the effective description
      // for the current UI language — no extra translate API call needed.
      const seededTranslations: Record<string, Partial<IHarborData>> = {};
      if (
        harborData.translations &&
        typeof harborData.translations === 'object'
      ) {
        Object.entries(
          harborData.translations as Record<string, { description?: string }>
        ).forEach(([code, val]) => {
          seededTranslations[code] = { description: val?.description ?? '' };
        });
        setTranslations(seededTranslations);
      }

      // Pick the current-language description when available, else the raw description
      const effectiveDescription =
        seededTranslations[currentLanguage.code]?.description ??
        harborData.description ??
        '';

      const rawVhfChannel = (harborData as unknown as { vhf_channel?: unknown })
        .vhf_channel;
      const normalizedVhfChannel =
        rawVhfChannel == null
          ? undefined
          : typeof rawVhfChannel === 'string'
            ? rawVhfChannel.trim() === ''
              ? undefined
              : rawVhfChannel.trim()
            : rawVhfChannel;

      initialVhfChannelRef.current = normalizedVhfChannel;

      setFormData({
        ...harborData,
        description: effectiveDescription,
        vhf_channel:
          normalizedVhfChannel as Partial<IHarborData>['vhf_channel'],
        contact_phone: harborData.contact_phone
          ? normalizeNationalPhoneNumber(
              harborData.contact_phone,
              resolvedPhoneCountryShort
            )
          : '',
        country_code: resolvedPhoneCountryCode || '+358',
        country_short_code: resolvedCountryShort,
        contact_phone_country_short_code: resolvedPhoneCountryShort,
        product_vat_config_id: harborData.product_vat_config_id ?? null,
        clubId: harborData.club?.id || '',
        club: harborData.club,
        regionIds: harborData.regions?.map((region) => region.id) || [],
        serviceIds:
          harborData.services?.map((service) => service.id) ||
          harborData.serviceIds ||
          [],
        send_booking_to_admin: harborData.send_booking_to_admin ?? true,
        mediation_response_deadline_hours:
          harborData.mediation_response_deadline_hours ?? 48,
        booking_enabled: harborData.booking_enabled ?? false,
        booking_type: harborData.booking_type || HarborBookingType.RESERVATION,
        default_check_in_time:
          harborData.default_check_in_time &&
          harborData.default_check_in_time.trim() !== ''
            ? harborData.default_check_in_time
            : '15:00',
        default_check_out_time:
          harborData.default_check_out_time &&
          harborData.default_check_out_time.trim() !== ''
            ? harborData.default_check_out_time
            : '12:00',
        check_in_window_start: harborData.check_in_window_start || '',
        check_in_window_end: harborData.check_in_window_end || '',
        check_out_window_start: harborData.check_out_window_start || '',
        check_out_window_end: harborData.check_out_window_end || '',
        timezone: effectiveTimezone,
        seasonal_dates: harborData.seasonal_dates
          ? {
              spring: Array.isArray(harborData.seasonal_dates.spring)
                ? harborData.seasonal_dates.spring.map(
                    (range: SeasonDateRange & { id?: string }) => ({
                      id: range.id || '', // Preserve existing ID or leave empty for new entries
                      start_date: range.start_date || '',
                      end_date: range.end_date || '',
                      is_closed: !!range.is_closed,
                    })
                  )
                : [],
              summer: Array.isArray(harborData.seasonal_dates.summer)
                ? harborData.seasonal_dates.summer.map(
                    (range: SeasonDateRange & { id?: string }) => ({
                      id: range.id || '',
                      start_date: range.start_date || '',
                      end_date: range.end_date || '',
                      is_closed: !!range.is_closed,
                    })
                  )
                : [],
              autumn: Array.isArray(harborData.seasonal_dates.autumn)
                ? harborData.seasonal_dates.autumn.map(
                    (range: SeasonDateRange & { id?: string }) => ({
                      id: range.id || '',
                      start_date: range.start_date || '',
                      end_date: range.end_date || '',
                      is_closed: !!range.is_closed,
                    })
                  )
                : [],
              winter: Array.isArray(harborData.seasonal_dates.winter)
                ? harborData.seasonal_dates.winter.map(
                    (range: SeasonDateRange & { id?: string }) => ({
                      id: range.id || '',
                      start_date: range.start_date || '',
                      end_date: range.end_date || '',
                      is_closed: !!range.is_closed,
                    })
                  )
                : [],
            }
          : {
              spring: [],
              summer: [],
              autumn: [],
              winter: [],
            },
        facilities_with_conditions:
          harborData.facilities_with_conditions ||
          (harborData.facility_conditions
            ? harborData.facility_conditions.map(
                (fc: FacilityConditionFromApi) => ({
                  facility: fc.facility_name,
                  condition: fc.condition,
                  last_updated: fc.updated_at || new Date().toISOString(),
                  updated_by: fc.updated_by
                    ? {
                        id: fc.updated_by.id,
                        name:
                          fc.updated_by.name ||
                          fc.updated_by.email ||
                          'Unknown',
                      }
                    : undefined,
                  availability_status: fc.availability_status,
                  seasonal_availability: fc.seasonal_availability,
                })
              )
            : []),
        media_captions:
          harborData.media_captions && harborData.media_captions.length > 0
            ? harborData.media_captions.map((caption) => ({
                image_index: (caption as RawImageCaption).image_index,
                captions: (caption as RawImageCaption).translations,
              }))
            : [],
        fairway_map_captions:
          harborData.fairway_map_captions &&
          harborData.fairway_map_captions.length > 0
            ? harborData.fairway_map_captions.map((caption) => ({
                image_index: (caption as RawImageCaption).image_index,
                captions: (caption as RawImageCaption).translations,
              }))
            : [],
        fairway_entrance_map_captions:
          harborData.fairway_entrance_map_captions &&
          harborData.fairway_entrance_map_captions.length > 0
            ? harborData.fairway_entrance_map_captions.map((caption) => ({
                image_index: (caption as RawImageCaption).image_index,
                captions: (caption as RawImageCaption).translations,
              }))
            : [],
        berth_image_captions: harborData.berth_image_captions ?? [],
        terms_pdf: harborData.terms_pdf ?? null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [harborData, mode]);

  const [errors, setErrors] = useState<Record<string, string | string[]>>({});
  const [
    createHarbor,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      reset: resetCreateHarbor,
    },
  ] = useCreateHarborMutation();
  const [
    updateHarbor,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      reset: resetUpdateHarbor,
    },
  ] = useUpdateHarborMutation();
  const [
    createMediaHarborId,
    {
      isLoading: isUploadingMedia,
      isSuccess: uploadSuccess,
      isError: uploadError,
      error: uploadErrorDetails,
      reset: resetCreateMedia,
    },
  ] = useCreateMediaHarborIdMutation();
  const prevPhoneErrorRef = useRef(errors.contact_phone);
  const marinaDropdownRef = useRef<HTMLDivElement>(null);
  const marinaOptionsContainerRef = useRef<HTMLDivElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const countryTriggerRef = useRef<HTMLDivElement>(null);
  const countrySearchInputRef = useRef<HTMLInputElement>(null);
  const countryListboxRef = useRef<HTMLDivElement>(null);
  const countryFocusRequestRef = useRef<{
    type: 'search' | 'option';
    index: number;
  } | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const initialVhfChannelRef = useRef<unknown>(undefined);
  const [marinaOptions, setMarinaOptions] = useState<MarinaOption[]>([]);
  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([]);
  const [, setYachtClubOptions] = useState<YachtClubOption[]>([]);
  const [isMarinaDropdownOpen, setIsMarinaDropdownOpen] = useState(false);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [countryActiveIndex, setCountryActiveIndex] = useState(-1);
  const userType = useSelector(
    (state: RootState) => state?.auth?.userData?.roles
  );
  const currentUserId = useSelector(
    (state: RootState) => state?.auth?.userData?.id
  );
  const [updateFeaturedHarbor] = useUpdateFeaturedHarborMutation();

  // Check if the current user is the marina owner
  const isCurrentUserMarina =
    currentUserId && formData.marina_id && currentUserId === formData.marina_id;

  // Helper function to get available days for a season and month
  const getAvailableDays = (month: string) => {
    const monthNum = parseInt(month);
    // Return all days from 1 to the last day of the month
    return Array.from(
      { length: new Date(2000, monthNum, 0).getDate() },
      (_, i) => i + 1
    );
  };

  // Helper function to get translated month name
  const getTranslatedMonthName = (monthIndex: number) => {
    const monthKeys = [
      'COMMON.MONTHS.JAN',
      'COMMON.MONTHS.FEB',
      'COMMON.MONTHS.MAR',
      'COMMON.MONTHS.APR',
      'COMMON.MONTHS.MAY',
      'COMMON.MONTHS.JUN',
      'COMMON.MONTHS.JUL',
      'COMMON.MONTHS.AUG',
      'COMMON.MONTHS.SEP',
      'COMMON.MONTHS.OCT',
      'COMMON.MONTHS.NOV',
      'COMMON.MONTHS.DEC',
    ];
    return intl
      ? intl.formatMessage({ id: monthKeys[monthIndex - 1] })
      : formatDateWithOptions(
          new Date(0, monthIndex - 1),
          { month: 'short' },
          'en'
        );
  };
  const [showPreviewMessage, setShowPreviewMessage] = useState(false);
  const [translations, setTranslations] = useState<
    Record<string, Partial<IHarborData>>
  >({});

  // When the active UI language changes, swap the editor description to match
  // the translation slot for that language.
  useEffect(() => {
    const translatedDesc = translations[currentLanguage.code]?.description;
    if (translatedDesc !== undefined) {
      setFormData((prev) => ({ ...prev, description: translatedDesc }));
    }
    // We intentionally only react to language-code changes, not every translations change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage.code]);

  const [inactiveStartDate, setInactiveStartDate] = useState<string>('');
  const [inactiveEndDate, setInactiveEndDate] = useState<string>('');

  const marinaSearch = useDebounce(500);

  const {
    data: usersData,
    isLoading: isUsersLoading,
    error: usersError,
  } = useGetUsersQuery(
    {
      roles: [UserRoles.MARINA],
      search: marinaSearch.debouncedValue,
    },
    // Only admins can assign a harbor manager (and hold user-management read);
    // the current manager is shown from the harbor data.
    { skip: !userType?.some((role) => role === UserRoles.ADMIN) }
  ) as {
    data: PaginatedApiResponse<UserResponse[]> | undefined;
    isLoading: boolean;
    error: boolean;
  };

  const {
    data: regionsData,
    isLoading: isRegionsLoading,
    error: regionsError,
  } = useGetRegionsQuery(
    {
      status: Statuses.ACTIVE,
      language: currentLanguage.code,
    },
    {
      skip: !userType?.some((role) =>
        [UserRoles.ADMIN, UserRoles.MARINA].includes(role as UserRoles)
      ),
    }
  ) as {
    data: PaginatedApiResponse<UserResponse[]> | undefined;
    isLoading: boolean;
    error: boolean;
  };

  const [yachtClubsPagination] = useState({
    page: 1,
    limit: 10,
  });

  const { data: yachtsResponse } = useGetYachtsQuery({
    page: yachtClubsPagination.page,
    limit: yachtClubsPagination.limit,
    search: '',
  });

  const { data: coastalAreaData } = useGetCoastalAreaQuery({
    language: currentLanguage.code,
  });

  useEffect(() => {}, [coastalAreaData]);

  useEffect(() => {
    if (usersData?.data) {
      const options = usersData.data.map((user: UserResponse) => ({
        id: user.id,
        name: user.name,
        email: user.email,
      }));
      setMarinaOptions(options);

      if (mode === 'edit' && formData.marina_id) {
        const selectedMarina = options.find(
          (marina) => marina.id === formData.marina_id
        );
        if (
          selectedMarina &&
          (formData.marina?.id !== selectedMarina.id ||
            formData.marina?.name !== selectedMarina.name)
        ) {
          setFormData((prev) => ({
            ...prev,
            marina: {
              id: selectedMarina.id,
              name: selectedMarina.name,
            },
            marina_id: selectedMarina.id,
          }));
          validateField('marina_id', selectedMarina.id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    usersData,
    mode,
    formData.marina_id,
    formData.marina,
    setFormData,
    // validateField is defined later in the file and is stable (wrapped in useCallback)
  ]);

  useEffect(() => {
    if (regionsData?.data) {
      const options = regionsData.data.map((user: UserResponse) => ({
        value: user.id,
        label: user.name,
      }));
      setRegionOptions(options);
    }
  }, [regionsData]);

  useEffect(() => {
    if (yachtsResponse?.data) {
      const yachtClubOptions: YachtClubOption[] =
        (yachtsResponse?.data as unknown as YachtResponse[])?.map((yacht) => ({
          id: yacht.id,
          name: yacht.name,
          label: yacht.name,
          value: yacht.id,
        })) || [];
      setYachtClubOptions(yachtClubOptions);
    }
  }, [yachtsResponse]);

  useEffect(() => {
    if (usersError) {
      console.error('Failed to load marinas:', usersError);
    }
    if (regionsError) {
      console.error('Failed to load regions:', regionsError);
    }
  }, [usersError, regionsError]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        marinaDropdownRef.current &&
        !marinaDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMarinaDropdownOpen(false);
      }
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCountryDropdownOpen(false);
        setCountrySearchTerm('');
        setCountryActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const countriesList = useMemo(
    () => localizedCountryOptions(currentLanguage.code || 'en'),
    [currentLanguage.code]
  );

  const filteredCountries = useMemo(() => {
    const term = countrySearchTerm.toLowerCase();
    return countriesList.filter(
      (country) =>
        country.name.toLowerCase().includes(term) ||
        country.code.toLowerCase().includes(term)
    );
  }, [countrySearchTerm, countriesList]);

  // Autofocus the Name field when the form opens
  useEffect(() => {
    const t = setTimeout(() => nameInputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!formData.clubId) {
      setFormData((prev) => ({
        ...prev,
        clubId: '',
      }));
    }
  }, [formData.clubId]);

  // Check if name field is filled to show preview button
  useEffect(() => {
    const isNameFilled = !!formData.name && formData.name.trim() !== '';
    setShowPreviewMessage(isNameFilled);
  }, [formData.name]);

  const handleOpenPreview = useCallback(() => {
    setIsPreviewModalOpen(true);
  }, []);

  const handleTranslationsGenerated = useCallback(
    (generatedTranslations: Record<string, Partial<IHarborData>>) => {
      setTranslations(generatedTranslations);

      // If the currently active language's description was updated from the modal,
      // sync it back to the main form description editor instantly.
      const currentLangDesc =
        generatedTranslations[currentLanguage.code]?.description;
      if (currentLangDesc !== undefined) {
        setFormData((prev) => {
          if (prev.description !== currentLangDesc) {
            return { ...prev, description: currentLangDesc };
          }
          return prev;
        });
      }
    },
    [currentLanguage.code]
  );

  useEffect(() => {
    if (
      (createSuccess && (!formData.media?.length || uploadSuccess)) ||
      updateSuccess
    ) {
      enqueueSnackbar(
        mode === 'create'
          ? intl.formatMessage({ id: 'HARBOR.CREATED_SUCCESSFULLY' })
          : intl.formatMessage({ id: 'HARBOR.UPDATED_SUCCESSFULLY' }),
        { variant: 'success' }
      );
      navigate('/habor-management');
      resetCreateHarbor();
      resetUpdateHarbor();
      resetCreateMedia();
    }
  }, [
    createSuccess,
    updateSuccess,
    uploadSuccess,
    formData.media,
    enqueueSnackbar,
    navigate,
    resetCreateHarbor,
    resetUpdateHarbor,
    resetCreateMedia,
    mode,
    intl,
  ]);

  useEffect(() => {
    if (uploadError) {
      const errorMessage =
        (uploadErrorDetails as ErrorApiResponse)?.data?.message ||
        intl.formatMessage({ id: 'HARBOR.FAILED_TO_UPLOAD_MEDIA' });
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, [uploadError, uploadErrorDetails, enqueueSnackbar, intl]);

  const validateField = useCallback(
    async (name: keyof IHarborData | string, value: unknown) => {
      try {
        const schema = Yup.object().shape({
          name: Yup.string()
            .test(
              'no-leading-space',
              intl
                ? intl.formatMessage({ id: 'VALIDATION.NO_LEADING_SPACE' })
                : 'Cannot start with spaces',
              (value) => {
                if (!value) return true;
                return !value.startsWith(' ');
              }
            )
            .min(
              2,
              intl
                ? intl.formatMessage({ id: 'HARBOR.VALIDATION.NAME_MIN' })
                : 'Harbor Name must be at least 2 characters'
            )
            .max(
              50,
              intl
                ? intl.formatMessage({ id: 'HARBOR.VALIDATION.NAME_MAX' })
                : 'Harbor Name must be at most 50 characters'
            )
            .required(
              intl
                ? intl.formatMessage({ id: 'HARBOR.VALIDATION.NAME_REQUIRED' })
                : 'Harbor Name is required'
            ),
          status: Yup.string().required(
            intl
              ? intl.formatMessage({ id: 'HARBOR.VALIDATION.STATUS_REQUIRED' })
              : 'Status is required'
          ),
          booking_type: Yup.string()
            .oneOf(Object.values(HarborBookingType))
            .notRequired(),
          category: (() => {
            let categorySchema = Yup.string()
              .typeError(
                intl
                  ? intl.formatMessage({
                      id: 'HARBOR.VALIDATION.CATEGORY_INVALID',
                    })
                  : 'Category is required'
              )
              .required(
                intl
                  ? intl.formatMessage({
                      id: 'HARBOR.VALIDATION.CATEGORY_INVALID',
                    })
                  : 'Category is required'
              );

            if (harborCategoryKeys.length > 0) {
              categorySchema = categorySchema.oneOf(
                harborCategoryKeys,
                intl
                  ? intl.formatMessage({
                      id: 'HARBOR.VALIDATION.CATEGORY_INVALID',
                    })
                  : 'Invalid category'
              );
            }

            return categorySchema;
          })(),
          location: textFieldValidation(
            intl ? intl.formatMessage({ id: 'COMMON.LOCATION' }) : 'Location',
            5,
            200,
            true,
            intl
          ),
          latitude: Yup.number()
            .transform((value, originalValue) => {
              if (
                originalValue === '' ||
                originalValue === null ||
                originalValue === undefined
              ) {
                return undefined;
              }

              if (typeof originalValue === 'string') {
                const normalized = originalValue
                  .replace(/\s/g, '')
                  .replace(',', '.');
                const parsed = Number(normalized);
                return Number.isNaN(parsed) ? NaN : parsed;
              }

              return value;
            })
            .typeError(
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.LATITUDE_MUST_BE_NUMBER',
                  })
                : 'Latitude must be a number'
            )
            .min(
              -90,
              intl
                ? intl.formatMessage({ id: 'HARBOR.VALIDATION.LATITUDE_MIN' })
                : 'Latitude must be at least -90'
            )
            .max(
              90,
              intl
                ? intl.formatMessage({ id: 'HARBOR.VALIDATION.LATITUDE_MAX' })
                : 'Latitude cannot exceed 90'
            )
            .required(
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.LATITUDE_REQUIRED',
                  })
                : 'Latitude is required'
            ),
          longitude: Yup.number()
            .transform((value, originalValue) => {
              if (
                originalValue === '' ||
                originalValue === null ||
                originalValue === undefined
              ) {
                return undefined;
              }

              if (typeof originalValue === 'string') {
                const normalized = originalValue
                  .replace(/\s/g, '')
                  .replace(',', '.');
                const parsed = Number(normalized);
                return Number.isNaN(parsed) ? NaN : parsed;
              }

              return value;
            })
            .typeError(
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.LONGITUDE_MUST_BE_NUMBER',
                  })
                : 'Longitude must be a number'
            )
            .min(
              -180,
              intl
                ? intl.formatMessage({ id: 'HARBOR.VALIDATION.LONGITUDE_MIN' })
                : 'Longitude must be at least -180'
            )
            .max(
              180,
              intl
                ? intl.formatMessage({ id: 'HARBOR.VALIDATION.LONGITUDE_MAX' })
                : 'Longitude cannot exceed 180'
            )
            .required(
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.LONGITUDE_REQUIRED',
                  })
                : 'Longitude is required'
            ),
          description: textFieldValidation(
            intl
              ? intl.formatMessage({ id: 'COMMON.DESCRIPTION' })
              : 'Description',
            0,
            2000,
            false,
            intl
          ),
          contact_email: Yup.string().test(
            'optional-email',
            intl
              ? intl.formatMessage({
                  id: 'HARBOR.VALIDATION.INVALID_EMAIL_ADDRESS',
                })
              : 'Invalid email address',
            (value) => {
              if (!value || value.trim() === '') return true;
              return Yup.string().email().isValidSync(value);
            }
          ),
          contact_phone: Yup.string()
            .test(
              'is-valid-phone',
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.CONTACT_PHONE_NUMERIC',
                  })
                : 'Contact Phone is not a valid number',
              function (value) {
                if (!value || value.trim() === '') return true;
                return isValidCanonicalPhone(
                  value,
                  this.parent?.contact_phone_country_short_code ||
                    this.parent?.country_short_code,
                  this.parent?.country_code
                );
              }
            )
            .when('category', {
              is: (cat: string) =>
                ['guest_harbour', 'municipal_guest_harbour'].includes(cat),
              then: (schema) =>
                schema.required(
                  intl
                    ? intl.formatMessage({
                        id: 'HARBOR.VALIDATION.CONTACT_PHONE_REQUIRED',
                      })
                    : 'Contact Phone is required'
                ),
              otherwise: (schema) => schema.notRequired(),
            }),
          country_code: Yup.string().test(
            'optional-country-code',
            intl
              ? intl.formatMessage({
                  id: 'HARBOR.VALIDATION.INVALID_COUNTRY_CODE',
                })
              : 'Invalid country code (e.g., +1, +91)',
            (value) => {
              if (!value || value === '') return true;
              return /^\+\d{1,3}$/.test(value);
            }
          ),
          country_short_code: Yup.string().required(
            intl
              ? intl.formatMessage(
                  { id: 'VALIDATION.REQUIRED' },
                  { field: 'Country' }
                )
              : 'Country is required'
          ),
          whatsapp_link: Yup.string()
            .url(
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.MUST_BE_VALID_URL',
                  })
                : 'Must be a valid URL'
            )
            .notRequired()
            .nullable(),
          facebook_link: Yup.string()
            .url(
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.MUST_BE_VALID_URL',
                  })
                : 'Must be a valid URL'
            )
            .notRequired()
            .nullable(),
          instagram_link: Yup.string()
            .url(
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.MUST_BE_VALID_URL',
                  })
                : 'Must be a valid URL'
            )
            .notRequired()
            .nullable(),
          pdf_link: Yup.string()
            .url(
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.MUST_BE_VALID_URL',
                  })
                : 'Must be a valid URL'
            )
            .notRequired()
            .nullable(),
          terms_pdf: Yup.mixed().notRequired().nullable(),
          website_url: Yup.string()
            .url(
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.MUST_BE_VALID_URL',
                  })
                : 'Must be a valid URL'
            )
            .max(
              200,
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.WEBSITE_MAX_LENGTH',
                  })
                : 'Website must be at most 200 characters'
            )
            .notRequired()
            .nullable(),
          berth_capacity: Yup.number()
            .typeError(
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.BERTH_CAPACITY_MUST_BE_NUMBER',
                  })
                : 'Berth Capacity must be a number'
            )
            .min(
              1,
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.BERTH_CAPACITY_MIN',
                  })
                : 'Berth Capacity must be at least 1'
            )
            .max(
              10000,
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.BERTH_CAPACITY_MAX',
                  })
                : 'Berth Capacity cannot exceed 10000'
            )
            .notRequired(),
          min_depth: Yup.string().matches(
            /^\d+(?:[.,]\d{1,2})?$/,
            intl
              ? intl.formatMessage({ id: 'HARBOR.VALIDATION.MIN_DEPTH_FORMAT' })
              : "Minimum Depth must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
          ),
          max_depth: Yup.string()
            .matches(
              /^\d+(?:[.,]\d{1,2})?$/,
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.MAX_DEPTH_FORMAT',
                  })
                : "Maximum Depth must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
            )
            .test(
              'max-gte-min',
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.MAX_DEPTH_GTE_MIN',
                  })
                : 'Maximum Depth must be greater than or equal to Minimum Depth',
              function (value) {
                const { min_depth } = this.parent;
                if (!value || !min_depth) return true;
                const max = parseFloat(String(value).replace(',', '.'));
                const min = parseFloat(String(min_depth).replace(',', '.'));
                if (isNaN(max) || isNaN(min)) return true;
                return max >= min;
              }
            ),
          depth_unit: Yup.string()
            .oneOf(
              ['m', 'ft'],
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.INVALID_DEPTH_UNIT',
                  })
                : 'Invalid depth unit'
            )
            .notRequired(),
          default_check_in_time: Yup.string()
            .matches(
              /^([01]\d|2[0-3]):([0-5]\d)$/,
              'Check-in time must be in HH:mm format'
            )
            .notRequired(),
          default_check_out_time: Yup.string()
            .matches(
              /^([01]\d|2[0-3]):([0-5]\d)$/,
              'Check-out time must be in HH:mm format'
            )
            .notRequired(),
          check_in_window_start: Yup.string()
            .matches(
              /^([01]\d|2[0-3]):([0-5]\d)$/,
              'Check-in window start must be in HH:mm format'
            )
            .notRequired(),
          check_in_window_end: Yup.string()
            .matches(
              /^([01]\d|2[0-3]):([0-5]\d)$/,
              'Check-in window end must be in HH:mm format'
            )
            .test(
              'check-in-window-pair',
              'Both check-in window start and end are required',
              function (value) {
                const { check_in_window_start } = this.parent;
                return (
                  (!check_in_window_start && !value) ||
                  (!!check_in_window_start && !!value)
                );
              }
            )
            .test(
              'check-in-window-order',
              'Check-in window end must be after start',
              function (value) {
                const { check_in_window_start } = this.parent;
                if (!check_in_window_start || !value) return true;
                return value > check_in_window_start;
              }
            )
            .notRequired(),
          check_out_window_start: Yup.string()
            .matches(
              /^([01]\d|2[0-3]):([0-5]\d)$/,
              'Check-out window start must be in HH:mm format'
            )
            .notRequired(),
          check_out_window_end: Yup.string()
            .matches(
              /^([01]\d|2[0-3]):([0-5]\d)$/,
              'Check-out window end must be in HH:mm format'
            )
            .test(
              'check-out-window-pair',
              'Both check-out window start and end are required',
              function (value) {
                const { check_out_window_start } = this.parent;
                return (
                  (!check_out_window_start && !value) ||
                  (!!check_out_window_start && !!value)
                );
              }
            )
            .test(
              'check-out-window-order',
              'Check-out window end must be after start',
              function (value) {
                const { check_out_window_start } = this.parent;
                if (!check_out_window_start || !value) return true;
                return value > check_out_window_start;
              }
            )
            .notRequired(),
          timezone: Yup.string().notRequired(),
          facilities: Yup.array()
            .of(
              Yup.string().required(
                intl
                  ? intl.formatMessage({
                      id: 'HARBOR.VALIDATION.FACILITY_STRING',
                    })
                  : 'Facility must be a string'
              )
            )
            .notRequired()
            .nullable(),
          business_hours: businessHoursValidation(),
          logo: imageValidation(false, 5),
          fairway_map: imageValidation(false, 5),
          fairway_entrance_map: imageValidation(false, 5),
          berth_image: imageValidation(false, 5),
          media: Yup.array()
            .of(mediaValidation(false, 5, 10, 2, 1))
            .max(
              5,
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.MEDIA_MAX_FILES',
                  })
                : 'Maximum 5 media files allowed'
            )
            .notRequired(),
          marina_id: Yup.string().notRequired(),
          vhf_channel: Yup.string()
            .transform((value) => {
              const trimmed = typeof value === 'string' ? value.trim() : value;
              return trimmed === '' || trimmed == null ? undefined : trimmed;
            })
            .matches(
              /^\d+$/,
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.VHF_DIGITS_ONLY',
                  })
                : 'VHF Channel must contain only digits'
            )
            .min(
              1,
              intl
                ? intl.formatMessage({ id: 'HARBOR.VALIDATION.VHF_MIN_LENGTH' })
                : 'VHF Channel must be at least 1 digit'
            )
            .max(
              3,
              intl
                ? intl.formatMessage({ id: 'HARBOR.VALIDATION.VHF_MAX_LENGTH' })
                : 'VHF Channel must be at most 3 digits'
            )
            .notRequired(),
          regionIds: Yup.array()
            .of(
              Yup.string().required(
                intl
                  ? intl.formatMessage({
                      id: 'HARBOR.VALIDATION.REGION_ID_STRING',
                    })
                  : 'Region ID must be a string'
              )
            )
            .notRequired()
            .nullable(),
          yachtClubIds: Yup.array()
            .of(
              Yup.string().required(
                intl
                  ? intl.formatMessage({
                      id: 'HARBOR.VALIDATION.YACHT_CLUB_ID_STRING',
                    })
                  : 'Yacht Club ID must be a string'
              )
            )
            .notRequired()
            .nullable(),
          booking_enabled: Yup.boolean().notRequired(),
          send_booking_to_admin: Yup.boolean().required(
            intl
              ? intl.formatMessage({
                  id: 'HARBOR.VALIDATION.BOOKING_APPROVAL_REQUIRED',
                })
              : 'Please select an option for sending booking requests.'
          ),
          admin_notes: Yup.string().notRequired(),
          primary_language_spoken: Yup.string().notRequired(),
          coastal_area: Yup.string().required(
            intl
              ? intl.formatMessage({
                  id: 'HARBOR.VALIDATION.COASTAL_AREA_REQUIRED',
                })
              : 'Coastal Area is required'
          ),
          seasonality_support: Yup.boolean().notRequired(),
          seasonal_dates: Yup.object().when(
            'seasonality_support',
            (seasonality_support, schema) => {
              if (seasonality_support) {
                const noOverlapTest = (dates: unknown) => {
                  const baseYear = new Date().getFullYear();
                  const seasonalDates = dates as
                    | Partial<IHarborData>['seasonal_dates']
                    | undefined;
                  if (!seasonalDates) return true;

                  const parseMonthDay = (
                    isoDate: string
                  ): { month: number; day: number } | null => {
                    const trimmed = (isoDate || '').trim();
                    if (!trimmed) return null;

                    const parts = trimmed.split('-');
                    if (parts.length < 2) return null;

                    const monthPart = parts.length === 3 ? parts[1] : parts[0];
                    const dayPart = parts.length === 3 ? parts[2] : parts[1];

                    const month = Number(monthPart);
                    const day = Number(dayPart);
                    if (!Number.isFinite(month) || !Number.isFinite(day))
                      return null;
                    if (month < 1 || month > 12) return null;
                    if (day < 1 || day > 31) return null;

                    return { month, day };
                  };

                  const toDate = (y: number, m: number, d: number) =>
                    new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));

                  const resolveRangeToDates = (range: SeasonDateRange) => {
                    const start =
                      typeof range.start_date === 'string'
                        ? range.start_date
                        : '';
                    const end =
                      typeof range.end_date === 'string' ? range.end_date : '';

                    const startParts = start.split('-');
                    const endParts = end.split('-');
                    const hasFullYearStart =
                      startParts.length === 3 && startParts[0].length === 4;
                    const hasFullYearEnd =
                      endParts.length === 3 && endParts[0].length === 4;

                    if (hasFullYearStart && hasFullYearEnd) {
                      const s = new Date(`${start}T00:00:00Z`);
                      const e = new Date(`${end}T00:00:00Z`);
                      if (
                        Number.isNaN(s.getTime()) ||
                        Number.isNaN(e.getTime())
                      )
                        return null;
                      return { start: s, end: e };
                    }

                    const sMD = parseMonthDay(start);
                    const eMD = parseMonthDay(end);
                    if (!sMD || !eMD) return null;

                    const isCrossYear = sMD.month > eMD.month;
                    const sDate = toDate(baseYear, sMD.month, sMD.day);
                    const eDate = toDate(
                      isCrossYear ? baseYear + 1 : baseYear,
                      eMD.month,
                      eMD.day
                    );
                    return { start: sDate, end: eDate };
                  };

                  const seasons = [
                    'spring',
                    'summer',
                    'autumn',
                    'winter',
                  ] as const;
                  const intervals: Array<{ start: Date; end: Date }> = [];

                  for (const season of seasons) {
                    const ranges = (seasonalDates?.[season] ||
                      []) as SeasonDateRange[];
                    for (const r of ranges) {
                      const resolved = resolveRangeToDates(r);
                      if (!resolved) continue;
                      if (resolved.end.getTime() <= resolved.start.getTime())
                        continue;
                      intervals.push(resolved);
                    }
                  }

                  for (let i = 0; i < intervals.length; i++) {
                    for (let j = i + 1; j < intervals.length; j++) {
                      const a = intervals[i];
                      const b = intervals[j];
                      const intersects =
                        a.start.getTime() <= b.end.getTime() &&
                        b.start.getTime() <= a.end.getTime();
                      if (intersects) return false;
                    }
                  }

                  return true;
                };

                return Yup.object({
                  spring: Yup.array()
                    .of(
                      Yup.object({
                        id: Yup.string(),
                        start_date: Yup.string()
                          .required(
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.SPRING_START_DATE_REQUIRED',
                                })
                              : 'Spring start date is required'
                          )
                          .test(
                            'is-valid-date',
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.INVALID_DATE',
                                })
                              : 'Invalid date format',
                            (value) => !isNaN(Date.parse(value || ''))
                          ),
                        end_date: Yup.string()
                          .required(
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.SPRING_END_DATE_REQUIRED',
                                })
                              : 'Spring end date is required'
                          )
                          .test(
                            'is-valid-end-date',
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.SPRING_END_AFTER_START',
                                })
                              : 'End date must be after start date',
                            function (endDate) {
                              const { start_date } = this.parent;
                              if (!start_date || !endDate) return true;
                              return new Date(endDate) > new Date(start_date);
                            }
                          ),
                        is_closed: Yup.boolean().default(false),
                      })
                    )
                    .default(() => []),
                  summer: Yup.array()
                    .of(
                      Yup.object({
                        id: Yup.string(),
                        start_date: Yup.string()
                          .required(
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.SUMMER_START_DATE_REQUIRED',
                                })
                              : 'Summer start date is required'
                          )
                          .test(
                            'is-valid-date',
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.INVALID_DATE',
                                })
                              : 'Invalid date format',
                            (value) => !isNaN(Date.parse(value || ''))
                          ),
                        end_date: Yup.string()
                          .required(
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.SUMMER_END_DATE_REQUIRED',
                                })
                              : 'Summer end date is required'
                          )
                          .test(
                            'is-valid-end-date',
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.SUMMER_END_AFTER_START',
                                })
                              : 'End date must be after start date',
                            function (endDate) {
                              const { start_date } = this.parent;
                              if (!start_date || !endDate) return true;
                              return new Date(endDate) > new Date(start_date);
                            }
                          ),
                        is_closed: Yup.boolean().default(false),
                      })
                    )
                    .default(() => []),
                  autumn: Yup.array()
                    .of(
                      Yup.object({
                        id: Yup.string(),
                        start_date: Yup.string()
                          .required(
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.AUTUMN_START_DATE_REQUIRED',
                                })
                              : 'Autumn start date is required'
                          )
                          .test(
                            'is-valid-date',
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.INVALID_DATE',
                                })
                              : 'Invalid date format',
                            (value) => !isNaN(Date.parse(value || ''))
                          ),
                        end_date: Yup.string()
                          .required(
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.AUTUMN_END_DATE_REQUIRED',
                                })
                              : 'Autumn end date is required'
                          )
                          .test(
                            'is-valid-end-date',
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.AUTUMN_END_AFTER_START',
                                })
                              : 'End date must be after start date',
                            function (endDate) {
                              const { start_date } = this.parent;
                              if (!start_date || !endDate) return true;
                              return new Date(endDate) > new Date(start_date);
                            }
                          ),
                        is_closed: Yup.boolean().default(false),
                      })
                    )
                    .default(() => []),
                  winter: Yup.array()
                    .of(
                      Yup.object({
                        id: Yup.string(),
                        start_date: Yup.string()
                          .required(
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.WINTER_START_DATE_REQUIRED',
                                })
                              : 'Winter start date is required'
                          )
                          .test(
                            'is-valid-date',
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.INVALID_DATE',
                                })
                              : 'Invalid date format',
                            (value) => !isNaN(Date.parse(value || ''))
                          ),
                        end_date: Yup.string()
                          .required(
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.WINTER_END_DATE_REQUIRED',
                                })
                              : 'Winter end date is required'
                          )
                          .test(
                            'is-valid-end-date',
                            intl
                              ? intl.formatMessage({
                                  id: 'HARBOR.VALIDATION.WINTER_END_AFTER_START',
                                })
                              : 'End date must be after start date',
                            function (endDate) {
                              const { start_date } = this.parent;
                              if (!start_date || !endDate) return true;
                              return new Date(endDate) > new Date(start_date);
                            }
                          ),
                        is_closed: Yup.boolean().default(false),
                      })
                    )
                    .default(() => []),
                }).test(
                  'no-date-overlap',
                  intl
                    ? intl.formatMessage({
                        id: 'HARBOR.VALIDATION.DATE_RANGES_OVERLAP',
                      })
                    : 'Date ranges cannot overlap',
                  function (value) {
                    const ok = noOverlapTest(value);
                    if (ok) return true;
                    return this.createError({
                      path: 'seasonal_dates',
                      message: intl
                        ? intl.formatMessage({
                            id: 'HARBOR.VALIDATION.DATE_RANGES_OVERLAP',
                          })
                        : 'Date ranges cannot overlap',
                    });
                  }
                );
              }
              return schema.notRequired();
            }
          ),
        });

        await schema.validateAt(
          name,
          { ...formData, [name]: value },
          { abortEarly: true }
        );
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => {
            const newError =
              err.errors[0] ||
              intl.formatMessage({ id: 'VALIDATION.INVALID_INPUT' });
            if (prev[name] === newError) return prev;
            return { ...prev, [name]: newError };
          });
        }
      }
    },
    [setErrors, formData, harborCategoryKeys, intl]
  );

  const validatePhoneNumber = useCallback(
    async (phone: string, shortCode: string) => {
      try {
        const isRequired = [
          'guest_harbour',
          'municipal_guest_harbour',
        ].includes(formData.category || '');
        const schema = Yup.object().shape({
          contact_phone: Yup.string()
            .test(
              'is-valid-phone',
              intl
                ? intl.formatMessage({
                    id: 'HARBOR.VALIDATION.CONTACT_PHONE_NUMERIC',
                  })
                : 'Contact Phone is not a valid number',
              function (value) {
                if (!value || value.trim() === '') return true;
                return isValidCanonicalPhone(
                  value,
                  shortCode,
                  formData.country_code
                );
              }
            )
            .when([], {
              is: () => isRequired,
              then: (schema) =>
                schema.required(
                  intl
                    ? intl.formatMessage({
                        id: 'HARBOR.VALIDATION.CONTACT_PHONE_REQUIRED',
                      })
                    : 'Contact Phone is required'
                ),
              otherwise: (schema) => schema.notRequired(),
            }),
        });

        const tempFormData = {
          contact_phone: phone,
          country_short_code: shortCode,
          country_code: formData.country_code,
        };

        await schema.validateAt('contact_phone', tempFormData, {
          abortEarly: true,
        });
        if (prevPhoneErrorRef.current !== '') {
          setErrors((prev) => {
            if (prev.contact_phone === '') return prev;
            return { ...prev, contact_phone: '' };
          });
          prevPhoneErrorRef.current = '';
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const newError =
            err.errors[0] ||
            intl.formatMessage({ id: 'VALIDATION.INVALID_INPUT' });
          if (prevPhoneErrorRef.current !== newError) {
            setErrors((prev) => {
              if (prev.contact_phone === newError) return prev;
              return { ...prev, contact_phone: newError };
            });
            prevPhoneErrorRef.current = newError;
          }
        }
      }
    },
    [setErrors, formData.category, formData.country_code, intl]
  );

  useEffect(() => {
    const activeShortCode = formData.contact_phone_country_short_code || formData.country_short_code;
    if (activeShortCode) {
      validatePhoneNumber(
        formData.contact_phone || '',
        activeShortCode
      );
    } else if (!formData.contact_phone && errors.contact_phone) {
      setErrors((prev) => {
        if (!prev.contact_phone) return prev;
        return { ...prev, contact_phone: '' };
      });
      prevPhoneErrorRef.current = '';
    }
  }, [
    formData.contact_phone,
    formData.country_short_code,
    formData.contact_phone_country_short_code,
    formData.country_code,
    validatePhoneNumber,
    errors.contact_phone,
  ]);

  // Re-validate fields with errors when language changes
  const prevLocaleRef = useRef(intl.locale);

  const shouldShowBusinessEntityVatField = Boolean(formData.country_short_code);

  const {
    data: businessEntityVatConfigsResponse,
    isLoading: isBusinessEntityVatConfigsLoading,
  } = useGetHarborVatRegimesQuery(formData.country_short_code ?? '', {
    skip: !shouldShowBusinessEntityVatField,
  });

  const { data: selectedBusinessEntityVatConfig } =
    useGetHarborVatRegimeByIdQuery(formData.product_vat_config_id as string, {
      skip:
        !shouldShowBusinessEntityVatField ||
        !formData.product_vat_config_id ||
        (businessEntityVatConfigsResponse?.data ?? []).some(
          (config) => config.id === formData.product_vat_config_id
        ),
    });

  const businessEntityVatOptions = React.useMemo(() => {
    const configs = [...(businessEntityVatConfigsResponse?.data ?? [])];

    if (
      selectedBusinessEntityVatConfig &&
      !configs.some(
        (config) => config.id === selectedBusinessEntityVatConfig.id
      )
    ) {
      configs.push(selectedBusinessEntityVatConfig);
    }

    return configs
      .filter(
        (config: HarborVatRegime) =>
          isProductVatConfigActive(config) ||
          config.id === formData.product_vat_config_id
      )
      .map((config: HarborVatRegime) => ({
        value: config.id,
        label: `${formatFinnishPercentage(config.default_vat_rate)} - Business entity ${
          formData.country_short_code ? `(${formData.country_short_code})` : ''
        }${isProductVatConfigActive(config) ? '' : ' (Inactive)'}`,
      }));
  }, [
    businessEntityVatConfigsResponse?.data,
    formData.country_short_code,
    formData.product_vat_config_id,
    selectedBusinessEntityVatConfig,
  ]);

  useEffect(() => {
    if (prevLocaleRef.current !== intl.locale) {
      prevLocaleRef.current = intl.locale;

      // Re-validate all fields that currently have errors
      const fieldsWithErrors = Object.keys(errors).filter(
        (field) => errors[field]
      );
      if (fieldsWithErrors.length > 0) {
        fieldsWithErrors.forEach((fieldName) => {
          const fieldValue = formData[fieldName as keyof IHarborData];
          validateField(fieldName, fieldValue);
        });
      }
    }
  }, [intl.locale, errors, formData, validateField]);

  useEffect(() => {
    const selectedConfigCountryCode =
      selectedBusinessEntityVatConfig?.domestic_country_code?.toUpperCase() ??
      null;
    const formCountryCode = formData.country_short_code?.toUpperCase() ?? null;

    if (
      !formData.product_vat_config_id ||
      !formCountryCode ||
      !selectedConfigCountryCode ||
      selectedConfigCountryCode === formCountryCode
    ) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      product_vat_config_id: null,
    }));
  }, [
    formData.country_short_code,
    formData.product_vat_config_id,
    selectedBusinessEntityVatConfig,
  ]);

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value } = e.target;
      const key = name as keyof IHarborData;
      let parsedValue: unknown = value;
      if (['latitude', 'longitude', 'berth_capacity'].includes(name)) {
        parsedValue = value === '' ? undefined : parseFloat(value);
      } else if (name === 'vhf_channel') {
        const trimmed = value.trim();
        parsedValue = trimmed === '' ? undefined : trimmed;
      } else if (name === 'product_vat_config_id') {
        parsedValue = value || null;
      } else if (name === 'mediation_response_deadline_hours') {
        const n = parseInt(value, 10);
        parsedValue = Number.isNaN(n)
          ? undefined
          : Math.min(168, Math.max(1, n));
      } else if (['send_booking_to_admin', 'booking_enabled'].includes(name)) {
        parsedValue = value === 'true';
      }
      if (['min_depth', 'max_depth'].includes(name)) {
        const sanitized = value.replace(/[^\d.,]/g, '');
        setFormData((prev) => ({
          ...prev,
          [name]: sanitized,
        }));
        validateField(name as keyof IHarborData, sanitized);
        return;
      }
      setFormData((prev) => {
        if (prev[key] === parsedValue) return prev;
        return { ...prev, [key]: parsedValue };
      });
      validateField(key, parsedValue);
    },
    [setFormData, validateField]
  );

  const handleInputBlur = useCallback(
    (name: string, value: unknown) => {
      validateField(name, value);
    },
    [validateField]
  );

  const handleDialCodeChange = useCallback(
    (country_code: string, contact_phone_country_short_code?: string) => {
      setFormData((prev) => ({
        ...prev,
        country_code,
        contact_phone_country_short_code: contact_phone_country_short_code || prev.contact_phone_country_short_code || prev.country_short_code,
      }));
      if (country_code) {
        validateField('country_code', country_code);
      }
      if (contact_phone_country_short_code) {
        validateField('contact_phone_country_short_code', contact_phone_country_short_code);
      }
    },
    [setFormData, validateField]
  );

  const handleAddressCountryChange = useCallback(
    (dialCode: string, country_short_code: string) => {
      setFormData((prev) => {
        const newShortCode = country_short_code || '';
        return {
          ...prev,
          country_short_code: newShortCode,
        };
      });
      if (country_short_code) {
        validateField('country_short_code', country_short_code);
      }
    },
    [setFormData, validateField]
  );

  const isCountryFieldDisabled = isCreating || isUploadingMedia;

  const countryActiveOptionIndex =
    !isCountryDropdownOpen || filteredCountries.length === 0
      ? -1
      : countryActiveIndex < 0
        ? 0
        : Math.min(countryActiveIndex, filteredCountries.length - 1);

  const countryActiveDescendantId =
    countryActiveOptionIndex >= 0
      ? `harbor-country-option-${filteredCountries[countryActiveOptionIndex]?.code}`
      : undefined;

  const closeCountryDropdown = useCallback((restoreFocus = false) => {
    setIsCountryDropdownOpen(false);
    setCountrySearchTerm('');
    setCountryActiveIndex(-1);
    if (restoreFocus) {
      countryTriggerRef.current?.focus();
    }
  }, []);

  const selectCountryOption = useCallback(
    (countryCode: string) => {
      const countryData = countries.find((c) => c.cca2 === countryCode);
      const dialCode = countryData
        ? `${countryData.idd.root || ''}${countryData.idd.suffixes?.[0] || ''}`
        : '';
      handleAddressCountryChange(dialCode, countryCode);
      setIsCountryDropdownOpen(false);
      setCountrySearchTerm('');
      setCountryActiveIndex(-1);
      countryTriggerRef.current?.focus();
    },
    [handleAddressCountryChange]
  );

  const focusCountryOption = useCallback((index: number) => {
    const option =
      countryListboxRef.current?.querySelectorAll<HTMLElement>(
        '[role="option"]'
      )[index];
    if (!option) return;
    setCountryActiveIndex(index);
    option.focus();
    option.scrollIntoView({ block: 'nearest' });
  }, []);

  const openCountryDropdown = useCallback(
    (focus: 'search' | 'option', edge?: 'last') => {
      if (isCountryFieldDisabled) return;
      let index = -1;
      if (filteredCountries.length > 0) {
        if (edge === 'last') {
          index = filteredCountries.length - 1;
        } else {
          const selectedIndex = filteredCountries.findIndex(
            (country) => country.code === formData.country_short_code
          );
          index = selectedIndex >= 0 ? selectedIndex : 0;
        }
      }
      setCountryActiveIndex(index);
      countryFocusRequestRef.current = { type: focus, index };
      setIsCountryDropdownOpen(true);
    },
    [
      filteredCountries,
      formData.country_short_code,
      isCountryFieldDisabled,
    ]
  );

  useEffect(() => {
    if (!isCountryDropdownOpen) return;
    const frame = requestAnimationFrame(() => {
      const request = countryFocusRequestRef.current;
      if (!request) return;
      countryFocusRequestRef.current = null;
      if (request.type === 'search' || request.index < 0) {
        countrySearchInputRef.current?.focus();
        return;
      }
      const option =
        countryListboxRef.current?.querySelectorAll<HTMLElement>(
          '[role="option"]'
        )[request.index];
      option?.focus();
      option?.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [isCountryDropdownOpen]);

  const handleCountryDropdownBlur = (
    event: React.FocusEvent<HTMLDivElement>
  ) => {
    const next = event.relatedTarget as Node | null;
    if (next && countryDropdownRef.current?.contains(next)) return;

    // relatedTarget is sometimes null while focus is moving to an option.
    // Confirm on the next frame so Escape, Tab, and outside clicks still close,
    // without collapsing the list during an in-widget focus move.
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active && countryDropdownRef.current?.contains(active)) return;
      setIsCountryDropdownOpen(false);
      setCountrySearchTerm('');
      setCountryActiveIndex(-1);
    });
  };

  const handleCountryDropdownKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (isCountryFieldDisabled) return;

    const target = event.target as HTMLElement;
    const optionEl = target.closest('[role="option"]') as HTMLElement | null;
    const isSearch =
      target === countrySearchInputRef.current || target.tagName === 'INPUT';
    const isOption = !!optionEl;

    if (event.key === 'Escape') {
      if (!isCountryDropdownOpen) return;
      event.preventDefault();
      event.stopPropagation();
      closeCountryDropdown(true);
      return;
    }

    if (isSearch) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (filteredCountries.length === 0) return;
        const nextIndex =
          countryActiveOptionIndex >= 0 ? countryActiveOptionIndex : 0;
        focusCountryOption(nextIndex);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (countryActiveOptionIndex > 0) {
          focusCountryOption(countryActiveOptionIndex - 1);
        } else {
          countryTriggerRef.current?.focus();
        }
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const selected = filteredCountries[countryActiveOptionIndex];
        if (selected) selectCountryOption(selected.code);
      } else if (event.key === 'Home' && event.altKey) {
        event.preventDefault();
        if (filteredCountries.length > 0) focusCountryOption(0);
      } else if (event.key === 'End' && event.altKey) {
        event.preventDefault();
        if (filteredCountries.length > 0) {
          focusCountryOption(filteredCountries.length - 1);
        }
      }
      return;
    }

    if (isOption && optionEl) {
      const index = Number(optionEl.dataset.index);
      if (!Number.isInteger(index)) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (filteredCountries.length === 0) return;
        const nextIndex =
          index + 1 >= filteredCountries.length ? 0 : index + 1;
        focusCountryOption(nextIndex);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (index <= 0) {
          countrySearchInputRef.current?.focus();
        } else {
          focusCountryOption(index - 1);
        }
      } else if (event.key === 'Home') {
        event.preventDefault();
        focusCountryOption(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        if (filteredCountries.length > 0) {
          focusCountryOption(filteredCountries.length - 1);
        }
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const selected = filteredCountries[index];
        if (selected) selectCountryOption(selected.code);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isCountryDropdownOpen) {
        openCountryDropdown('option');
      } else if (filteredCountries.length > 0) {
        focusCountryOption(
          countryActiveOptionIndex >= 0 ? countryActiveOptionIndex : 0
        );
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isCountryDropdownOpen) {
        openCountryDropdown('option', 'last');
      } else if (filteredCountries.length > 0) {
        focusCountryOption(
          countryActiveOptionIndex >= 0
            ? countryActiveOptionIndex
            : filteredCountries.length - 1
        );
      }
    } else if (event.key === 'Home') {
      if (!isCountryDropdownOpen || filteredCountries.length === 0) return;
      event.preventDefault();
      focusCountryOption(0);
    } else if (event.key === 'End') {
      if (!isCountryDropdownOpen || filteredCountries.length === 0) return;
      event.preventDefault();
      focusCountryOption(filteredCountries.length - 1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!isCountryDropdownOpen) {
        openCountryDropdown('search');
      } else {
        const selected = filteredCountries[countryActiveOptionIndex];
        if (selected) {
          selectCountryOption(selected.code);
        } else {
          closeCountryDropdown(true);
        }
      }
    }
  };

  const handleMarinaDropdownToggle = () => {
    if (isUsersLoading || isCurrentUserMarina) return;
    setIsMarinaDropdownOpen(!isMarinaDropdownOpen);
  };

  const handleMarinaSelect = (marina: MarinaOption) => {
    setFormData((prev) => {
      const updatedMarina = { id: marina.id, name: marina.name };
      return {
        ...prev,
        marina: updatedMarina,
        marina_id: marina.id,
        manager: updatedMarina,
      };
    });
    setIsMarinaDropdownOpen(false);
  };

  const handleRegionChange = (newValue: MultiValue<RegionOption>) => {
    const selectedRegionIds = newValue.map((option) => option.value);
    setFormData((prev) => ({
      ...prev,
      regionIds: selectedRegionIds,
    }));
    validateField('regionIds', selectedRegionIds);
  };
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const handleNumberInputFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.addEventListener('wheel', (event) => event.preventDefault(), {
        passive: false,
      });
    },
    []
  );

  const handleNumberInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.removeEventListener('wheel', (event) => event.preventDefault());
      const { name, value } = e.target;
      const parsedValue = value === '' ? undefined : parseFloat(value);
      handleInputBlur(name, parsedValue);
    },
    [handleInputBlur]
  );

  const handleSubmit = async (isDraft = false) => {
    try {
      // Transform form data to ensure proper structure
      const transformFormData = (data: Partial<IHarborData>) => {
        if (!data) return data;

        const transformSeason = (
          season: unknown
        ): Array<SeasonDateRange & { id?: string }> => {
          if (!season) return [];
          if (Array.isArray(season)) {
            return (season as Array<SeasonDateRange & { id?: string }>).map(
              (item) => ({
                ...item,
                start_date:
                  typeof item.start_date === 'string'
                    ? item.start_date.replace(/"/g, '')
                    : item.start_date,
                end_date:
                  typeof item.end_date === 'string'
                    ? item.end_date.replace(/"/g, '')
                    : item.end_date,
                is_closed:
                  typeof item.is_closed === 'string'
                    ? item.is_closed === 'true'
                    : Boolean(item.is_closed),
              })
            );
          }
          return [];
        };

        return {
          ...data,
          seasonal_dates: data.seasonal_dates
            ? {
                spring: transformSeason(data.seasonal_dates.spring),
                summer: transformSeason(data.seasonal_dates.summer),
                autumn: transformSeason(data.seasonal_dates.autumn),
                winter: transformSeason(data.seasonal_dates.winter),
              }
            : { spring: [], summer: [], autumn: [], winter: [] },
        };
      };

      const resolveSeasonalDatesForPayload = (
        seasonalDates: Partial<IHarborData>['seasonal_dates']
      ): Partial<IHarborData>['seasonal_dates'] => {
        const baseYear = new Date().getFullYear();
        if (!seasonalDates) return seasonalDates;

        const parseMonthDay = (
          isoDate: string
        ): { month: number; day: number } | null => {
          const trimmed = (isoDate || '').trim();
          if (!trimmed) return null;

          const parts = trimmed.split('-');
          if (parts.length < 2) return null;

          const monthPart = parts.length === 3 ? parts[1] : parts[0];
          const dayPart = parts.length === 3 ? parts[2] : parts[1];

          const month = Number(monthPart);
          const day = Number(dayPart);
          if (!Number.isFinite(month) || !Number.isFinite(day)) return null;
          if (month < 1 || month > 12) return null;
          if (day < 1 || day > 31) return null;

          return { month, day };
        };

        const formatWithYear = (year: number, month: number, day: number) => {
          const mm = String(month).padStart(2, '0');
          const dd = String(day).padStart(2, '0');
          return `${year}-${mm}-${dd}`;
        };

        const resolveRange = (range: SeasonDateRange & { id?: string }) => {
          const start =
            typeof range.start_date === 'string'
              ? parseMonthDay(range.start_date)
              : null;
          const end =
            typeof range.end_date === 'string'
              ? parseMonthDay(range.end_date)
              : null;

          if (!start || !end) return range;

          const isCrossYear = start.month > end.month;
          const resolvedStart = formatWithYear(
            baseYear,
            start.month,
            start.day
          );
          const resolvedEnd = formatWithYear(
            isCrossYear ? baseYear + 1 : baseYear,
            end.month,
            end.day
          );

          return {
            ...range,
            start_date: resolvedStart,
            end_date: resolvedEnd,
          };
        };

        const resolveSeasonArray = (season?: SeasonDateRange[]) => {
          if (!Array.isArray(season)) return season;
          return season.map((range) =>
            resolveRange(range as SeasonDateRange & { id?: string })
          );
        };

        return {
          ...seasonalDates,
          spring: resolveSeasonArray(seasonalDates.spring),
          summer: resolveSeasonArray(seasonalDates.summer),
          autumn: resolveSeasonArray(seasonalDates.autumn),
          winter: resolveSeasonArray(seasonalDates.winter),
        };
      };

      const transformedFormData = transformFormData(formData);
      const resolvedSeasonalDatesForSubmit = formData.seasonality_support
        ? resolveSeasonalDatesForPayload(transformedFormData.seasonal_dates)
        : transformedFormData.seasonal_dates;

      // If translations are missing, generate them before submit
      let translationsToUse = translations;
      if (Object.keys(translationsToUse).length === 0) {
        const translationPayload = {
          description: formData.description,
          source_language: currentLanguage.code || 'en',
        };
        try {
          const translationResult =
            await translateHarborContent(translationPayload).unwrap();
          if (translationResult && translationResult.translations) {
            translationsToUse = {};
            Object.entries(translationResult.translations).forEach(
              ([langCode, translation]) => {
                translationsToUse[langCode] = {
                  description: translation.description,
                };
              }
            );
            setTranslations(translationsToUse);
          }
        } catch {
          enqueueSnackbar(
            intl.formatMessage({
              id: 'HARBOR.FAILED_TO_GENERATE_TRANSLATIONS',
            }),
            { variant: 'error' }
          );
        }
      }
      // Skip validation for draft
      if (!isDraft) {
        const dynamicValidationSchema = Yup.object().shape({
          ...harborValidationSchema(intl).fields,
          logo: Yup.mixed().notRequired(),
          fairway_map: Yup.array().notRequired(),
          fairway_entrance_map: Yup.array().notRequired(),
          berth_image: Yup.mixed().notRequired(),
          media: Yup.array().notRequired(),
          seasonality_support: Yup.boolean(),
          seasonal_dates: Yup.object().when(
            'seasonality_support',
            (seasonality_support) => {
              if (seasonality_support) {
                return Yup.object().shape({
                  spring: Yup.array()
                    .of(
                      Yup.object().shape({
                        id: Yup.string(),
                        start_date: Yup.string()
                          .required(
                            intl.formatMessage(
                              { id: 'VALIDATION.REQUIRED' },
                              { field: 'Spring start date' }
                            )
                          )
                          .test(
                            'is-valid-date',
                            'Invalid date format',
                            (value) => !isNaN(Date.parse(value || ''))
                          ),
                        end_date: Yup.string()
                          .required(
                            intl.formatMessage(
                              { id: 'VALIDATION.REQUIRED' },
                              { field: 'Spring end date' }
                            )
                          )
                          .test(
                            'is-valid-end-date',
                            'End date must be after start date',
                            function (endDate) {
                              const { start_date } = this.parent;
                              if (!start_date || !endDate) return true;
                              return new Date(endDate) > new Date(start_date);
                            }
                          ),
                        is_closed: Yup.boolean().default(false),
                      })
                    )
                    .min(1, 'At least one date range is required for Spring'),
                  summer: Yup.array()
                    .of(
                      Yup.object().shape({
                        id: Yup.string(),
                        start_date: Yup.string()
                          .required(
                            intl.formatMessage(
                              { id: 'VALIDATION.REQUIRED' },
                              { field: 'Summer start date' }
                            )
                          )
                          .test(
                            'is-valid-date',
                            'Invalid date format',
                            (value) => !isNaN(Date.parse(value || ''))
                          ),
                        end_date: Yup.string()
                          .required(
                            intl.formatMessage(
                              { id: 'VALIDATION.REQUIRED' },
                              { field: 'Summer end date' }
                            )
                          )
                          .test(
                            'is-valid-end-date',
                            'End date must be after start date',
                            function (endDate) {
                              const { start_date } = this.parent;
                              if (!start_date || !endDate) return true;
                              return new Date(endDate) > new Date(start_date);
                            }
                          ),
                        is_closed: Yup.boolean().default(false),
                      })
                    )
                    .min(1, 'At least one date range is required for Summer'),
                  autumn: Yup.array()
                    .of(
                      Yup.object().shape({
                        id: Yup.string(),
                        start_date: Yup.string()
                          .required(
                            intl.formatMessage(
                              { id: 'VALIDATION.REQUIRED' },
                              { field: 'Autumn start date' }
                            )
                          )
                          .test(
                            'is-valid-date',
                            'Invalid date format',
                            (value) => !isNaN(Date.parse(value || ''))
                          ),
                        end_date: Yup.string()
                          .required(
                            intl.formatMessage(
                              { id: 'VALIDATION.REQUIRED' },
                              { field: 'Autumn end date' }
                            )
                          )
                          .test(
                            'is-valid-end-date',
                            'End date must be after start date',
                            function (endDate) {
                              const { start_date } = this.parent;
                              if (!start_date || !endDate) return true;
                              return new Date(endDate) > new Date(start_date);
                            }
                          ),
                        is_closed: Yup.boolean().default(false),
                      })
                    )
                    .min(1, 'At least one date range is required for Autumn'),
                  winter: Yup.array()
                    .of(
                      Yup.object().shape({
                        id: Yup.string(),
                        start_date: Yup.string()
                          .required(
                            intl.formatMessage(
                              { id: 'VALIDATION.REQUIRED' },
                              { field: 'Winter start date' }
                            )
                          )
                          .test(
                            'is-valid-date',
                            'Invalid date format',
                            (value) => !isNaN(Date.parse(value || ''))
                          ),
                        end_date: Yup.string()
                          .required(
                            intl.formatMessage(
                              { id: 'VALIDATION.REQUIRED' },
                              { field: 'Winter end date' }
                            )
                          )
                          .test(
                            'is-valid-end-date',
                            'End date must be after start date',
                            function (endDate) {
                              const { start_date } = this.parent;
                              if (!start_date || !endDate) return true;
                              return new Date(endDate) > new Date(start_date);
                            }
                          ),
                        is_closed: Yup.boolean().default(false),
                      })
                    )
                    .min(1, 'At least one date range is required for Winter'),
                });
              }
              return Yup.object().notRequired();
            }
          ),
          send_booking_to_admin: Yup.boolean().required(
            intl
              ? intl.formatMessage({
                  id: 'HARBOR.VALIDATION.BOOKING_APPROVAL_REQUIRED',
                })
              : 'Please select an option for sending booking requests.'
          ),
        });

        try {
          // Create a copy of form data for validation
          const validationData = {
            ...transformedFormData,
            seasonal_dates: resolvedSeasonalDatesForSubmit,
          };

          await dynamicValidationSchema.validate(validationData, {
            abortEarly: false,
            stripUnknown: true,
          });
        } catch (error: unknown) {
          const validationError = error as Yup.ValidationError;
          const validationErrors: Record<string, string[]> = {};

          // Process Yup validation errors
          if (validationError.inner) {
            validationError.inner.forEach((err) => {
              // Skip seasonal_dates errors if seasonality_support is false
              if (
                !formData.seasonality_support &&
                err.path?.startsWith('seasonal_dates')
              ) {
                return;
              }

              const path = err.path || 'form';
              if (!validationErrors[path]) {
                validationErrors[path] = [];
              }
              validationErrors[path].push(err.message);
            });
          }

          // Only show error if there are validation errors after filtering
          const errorPaths = Object.keys(validationErrors);
          if (errorPaths.length > 0) {
            // Show the first error in a toast
            const firstErrorPath = errorPaths[0];
            if (validationErrors[firstErrorPath]?.[0]) {
              enqueueSnackbar(validationErrors[firstErrorPath][0], {
                variant: 'error',
                autoHideDuration: 5000,
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'center',
                },
              });

              // Scroll to the first error field if possible
              setTimeout(() => {
                const errorElement = document.querySelector(
                  `[name="${firstErrorPath}"]`
                );
                if (
                  errorElement &&
                  typeof errorElement.scrollIntoView === 'function'
                ) {
                  try {
                    errorElement.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                  } catch (e) {
                    // Ignore scroll errors in test environment
                    if (process.env.NODE_ENV !== 'test') {
                      console.error('Error scrolling to element:', e);
                    }
                  }
                }
              }, 100);

              // Update form errors state (filtered to exclude seasonal dates when not needed)
              setErrors(validationErrors);
              return; // Stop form submission
            }
          }
        }
      }
      const payload = new FormData();
      if (formData.name) payload.append('name', formData.name);
      if (formData.description)
        payload.append('description', formData.description);
      if (formData.admin_notes)
        payload.append('admin_notes', formData.admin_notes);
      if (formData.location) payload.append('location', formData.location);
      if (formData.city) payload.append('city', formData.city);
      // Set harbor_status based on isDraft parameter for both create and edit modes
      const harborStatus = isDraft ? HarborStatus.DRAFT : HarborStatus.PUBLISH;
      payload.append('harbor_status', String(harborStatus));

      if (Number(formData.status) === Statuses.INACTIVE) {
        if (inactiveStartDate) {
          payload.append('start_date', inactiveStartDate);
        }
        if (inactiveEndDate) {
          payload.append('end_date', inactiveEndDate);
        }
      }
      // Add translations if available
      if (Object.keys(translationsToUse).length > 0) {
        payload.append('translations', JSON.stringify(translationsToUse));
      }
      if (formData.latitude != null)
        payload.append('latitude', String(formData.latitude));
      if (formData.longitude != null)
        payload.append('longitude', String(formData.longitude));
      if (formData.vhf_channel != null) {
        payload.append('vhf_channel', String(formData.vhf_channel));
      } else if (
        mode === 'edit' &&
        initialVhfChannelRef.current != null &&
        initialVhfChannelRef.current !== ''
      ) {
        payload.append('vhf_channel', '');
      }
      if (formData.berth_capacity != null)
        payload.append('berth_capacity', String(formData.berth_capacity));
      if (formData.min_depth != null)
        payload.append('min_depth', String(formData.min_depth));
      if (formData.max_depth != null)
        payload.append('max_depth', String(formData.max_depth));
      if (formData.depth_unit)
        payload.append('depth_unit', formData.depth_unit);
      const effectiveCheckInTime =
        formData.default_check_in_time &&
        formData.default_check_in_time.trim() !== ''
          ? formData.default_check_in_time
          : '15:00';
      payload.append('default_check_in_time', effectiveCheckInTime);
      const effectiveCheckOutTime =
        formData.default_check_out_time &&
        formData.default_check_out_time.trim() !== ''
          ? formData.default_check_out_time
          : '12:00';
      payload.append('default_check_out_time', effectiveCheckOutTime);
      if (formData.check_in_window_start?.trim()) {
        payload.append('check_in_window_start', formData.check_in_window_start);
      }
      if (formData.check_in_window_end?.trim()) {
        payload.append('check_in_window_end', formData.check_in_window_end);
      }
      if (formData.check_out_window_start?.trim()) {
        payload.append(
          'check_out_window_start',
          formData.check_out_window_start
        );
      }
      if (formData.check_out_window_end?.trim()) {
        payload.append('check_out_window_end', formData.check_out_window_end);
      }
      const timezoneFromForm =
        formData.timezone && formData.timezone.trim() !== ''
          ? formData.timezone
          : getDefaultTimezoneForCountry(formData.country_short_code);
      if (timezoneFromForm) {
        payload.append('timezone', timezoneFromForm);
      }
      payload.append('contact_email', formData.contact_email ?? '');
      payload.append(
        'contact_phone',
        normalizeNationalPhoneNumber(
          formData.contact_phone || '',
          formData.contact_phone_country_short_code || formData.country_short_code
        )
      );
      if (formData.country_code)
        payload.append('country_code', formData.country_code);
      if (formData.country_short_code)
        payload.append('country_short_code', formData.country_short_code);
      if (formData.contact_phone_country_short_code)
        payload.append('contact_phone_country_short_code', formData.contact_phone_country_short_code);
      payload.append(
        'product_vat_config_id',
        formData.product_vat_config_id ?? ''
      );
      if (formData.website_url)
        payload.append('website_url', formData.website_url);
      // Only append marina_id if the current user is not the marina owner
      if (formData.marina?.id && !isCurrentUserMarina) {
        payload.append('marina_id', formData.marina.id);
      }
      if (formData.category != null)
        payload.append('category', String(formData.category));
      if (formData.harbor_type)
        payload.append('harbor_type', formData.harbor_type);
      payload.append('regionIds', JSON.stringify(formData.regionIds ?? []));
      if (formData.clubId) {
        payload.append('clubId', formData.clubId);
      }

      if (formData.send_booking_to_admin !== undefined) {
        payload.append(
          'send_booking_to_admin',
          formData.send_booking_to_admin ? 'true' : 'false'
        );
      }

      if (formData.mediation_response_deadline_hours !== undefined) {
        payload.append(
          'mediation_response_deadline_hours',
          String(formData.mediation_response_deadline_hours)
        );
      }

      if (formData.booking_enabled !== undefined) {
        payload.append(
          'booking_enabled',
          formData.booking_enabled ? 'true' : 'false'
        );
      }

      if (formData.booking_type) {
        payload.append('booking_type', formData.booking_type);
      }

      if (formData.whatsapp_link)
        payload.append('whatsapp_link', formData.whatsapp_link);
      if (formData.facebook_link)
        payload.append('facebook_link', formData.facebook_link);
      if (formData.instagram_link)
        payload.append('instagram_link', formData.instagram_link);
      if (formData.pdf_link) payload.append('pdf_link', formData.pdf_link);

      if (formData.terms_pdf instanceof File) {
        payload.append('terms_pdf', formData.terms_pdf);
      } else if (typeof formData.terms_pdf === 'string' && formData.terms_pdf) {
        payload.append('terms_pdf', formData.terms_pdf);
      } else if (
        mode === 'edit' &&
        harborData?.terms_pdf &&
        !formData.terms_pdf
      ) {
        payload.append('terms_pdf', 'null');
      }

      if (formData.primary_language_spoken)
        payload.append(
          'primary_language_spoken',
          formData.primary_language_spoken
        );
      if (formData.coastal_area)
        payload.append('coastal_area', formData.coastal_area);
      if (formData.paytrail_merchant_id)
        payload.append('paytrail_merchant_id', formData.paytrail_merchant_id);

      if (formData.seasonality_support !== undefined) {
        payload.append(
          'seasonality_support',
          formData.seasonality_support ? 'true' : 'false'
        );
      }

      if (formData.seasonality_support && formData.seasonal_dates) {
        payload.append(
          'seasonal_dates',
          JSON.stringify(resolvedSeasonalDatesForSubmit)
        );
      }

      if (formData.businessHours) {
        try {
          const hours =
            typeof formData.businessHours === 'string'
              ? JSON.parse(formData.businessHours)
              : formData.businessHours;

          // Convert to simple object format if needed
          const formattedHours = Object.keys(hours).reduce(
            (acc, day) => {
              if (hours[day]?.open && hours[day]?.close) {
                acc[day] = {
                  open: hours[day].open,
                  close: hours[day].close,
                };
              }
              return acc;
            },
            {} as Record<string, { open: string; close: string }>
          );

          payload.append('business_hours', JSON.stringify(formattedHours));
        } catch (error) {
          console.error('Error processing business hours:', error);
        }
      }

      if (formData.facilities?.length) {
        formData.facilities.forEach((facility) =>
          payload.append('facilities[]', facility)
        );
      } else if (mode === 'edit') {
        // Explicitly clear facilities when updating an existing harbor
        payload.append('facilities', JSON.stringify([]));
      }

      // Add facility_conditions as array of objects (including availability fields)
      if (formData.facilities_with_conditions?.length) {
        const facilityConditions = formData.facilities_with_conditions.map(
          (facilityWithCondition) => ({
            facility_name: facilityWithCondition.facility,
            condition: facilityWithCondition.condition,
            availability_status: facilityWithCondition.availability_status,
            seasonal_availability:
              facilityWithCondition.availability_status === 'seasonal'
                ? facilityWithCondition.seasonal_availability
                : undefined,
          })
        );
        payload.append(
          'facility_conditions',
          JSON.stringify(facilityConditions)
        );
      } else if (mode === 'edit') {
        // Explicitly clear facility conditions when updating an existing harbor
        payload.append('facility_conditions', JSON.stringify([]));
      }

      if (formData.serviceIds?.length) {
        payload.append('serviceIds', JSON.stringify(formData.serviceIds));
      }

      if (formData.status) {
        payload.append(
          'status',
          Number(formData.status) === Statuses.ACTIVE
            ? '1'
            : Number(formData.status) === Statuses.UNDER_RENOVATION
              ? '4'
              : '0'
        );

        if (formData.logo instanceof File)
          payload.append('logo', formData.logo);
        else if (mode === 'edit' && harborData?.logo && !formData.logo)
          payload.append('logo', 'null');
        if (Array.isArray(formData.fairway_map)) {
          formData.fairway_map.forEach((file) => {
            if (file instanceof File) {
              payload.append('fairway_maps[]', file);
            } else if (typeof file === 'string') {
              payload.append('fairway_maps[]', file);
            }
          });
        }
        if (Array.isArray(formData.fairway_entrance_map)) {
          formData.fairway_entrance_map.forEach((file) => {
            if (file instanceof File) {
              payload.append('fairway_entrance_map[]', file);
            } else if (typeof file === 'string') {
              payload.append('fairway_entrance_map[]', file);
            }
          });
        }

        if (formData.media_captions?.length) {
          payload.append(
            'media_captions',
            JSON.stringify(formData.media_captions)
          );
        }
        if (formData.fairway_map_captions?.length) {
          payload.append(
            'fairway_map_captions',
            JSON.stringify(formData.fairway_map_captions)
          );
        }
        if (formData.fairway_entrance_map_captions?.length) {
          payload.append(
            'fairway_entrance_map_captions',
            JSON.stringify(formData.fairway_entrance_map_captions)
          );
        }
        if (formData.berth_image instanceof File) {
          payload.append('berth_image', formData.berth_image);
        } else if (
          typeof formData.berth_image === 'string' &&
          formData.berth_image
        ) {
          payload.append('berth_image', formData.berth_image);
        } else if (
          mode === 'edit' &&
          harborData?.berth_image &&
          !formData.berth_image
        ) {
          payload.append('berth_image', 'null');
        }
        if (formData.berth_image_captions?.length) {
          payload.append(
            'berth_image_captions',
            JSON.stringify(formData.berth_image_captions)
          );
        }

        let hasFeaturedChanges = false;

        if (mode === 'edit' && formData.id) {
          const mediaArray = Array.isArray(formData.media)
            ? formData.media
            : [];
          const originalMediaArray = Array.isArray(harborData?.media)
            ? harborData.media
            : [];

          const fairwayMapArray = Array.isArray(formData.fairway_map)
            ? formData.fairway_map
            : [];
          const originalFairwayMapArray = Array.isArray(harborData?.fairway_map)
            ? harborData.fairway_map
            : [];

          const fairwayEntranceMapArray = Array.isArray(
            formData.fairway_entrance_map
          )
            ? formData.fairway_entrance_map
            : [];
          const originalFairwayEntranceMapArray = Array.isArray(
            harborData?.fairway_entrance_map
          )
            ? harborData.fairway_entrance_map
            : [];

          const mediaToDelete = originalMediaArray.filter(
            (item) => typeof item === 'string' && !mediaArray.includes(item)
          );
          if (mediaToDelete.length > 0) {
            mediaToDelete.forEach((media) =>
              payload.append('mediaToDelete[]', media)
            );
          }

          const fairwayMapsToDelete = originalFairwayMapArray.filter(
            (item) =>
              typeof item === 'string' &&
              !fairwayMapArray.includes(item as string)
          );
          if (fairwayMapsToDelete.length > 0) {
            fairwayMapsToDelete.forEach((item) =>
              payload.append('fairwayMapsToDelete[]', item as string)
            );
          }

          const fairwayEntranceMapsToDelete =
            originalFairwayEntranceMapArray.filter(
              (item) =>
                typeof item === 'string' &&
                !fairwayEntranceMapArray.includes(item as string)
            );
          if (fairwayEntranceMapsToDelete.length > 0) {
            fairwayEntranceMapsToDelete.forEach((item) =>
              payload.append('fairwayEntranceMapsToDelete[]', item as string)
            );
          }

          const validMediaFiles = mediaArray.filter(
            (item) => item instanceof File
          ) as File[];

          let hasMediaChanges = false;
          if (validMediaFiles.length > 0) {
            const mediaPayload: MediaPayload = {
              id: formData.id,
              media: validMediaFiles,
            };
            await createMediaHarborId(mediaPayload).unwrap();
            hasMediaChanges = true;
          }

          if (
            Number(formData.status) === Statuses.INACTIVE &&
            formData.featured &&
            Number(harborData?.status) === Statuses.ACTIVE
          ) {
            await updateFeaturedHarbor({
              harborId: formData.id,
              featured: false,
            }).unwrap();
            hasFeaturedChanges = true;
            enqueueSnackbar(
              intl.formatMessage({
                id: 'HARBOR.REMOVED_FROM_FEATURED_DUE_TO_INACTIVE',
              }),
              {
                variant: 'info',
              }
            );
          }

          let hasOtherChanges = false;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          for (const pair of payload.entries()) {
            hasOtherChanges = true;
            break;
          }

          const hasChanges =
            hasMediaChanges || hasOtherChanges || hasFeaturedChanges;

          if (hasChanges) {
            if (
              hasOtherChanges ||
              mediaToDelete.length > 0 ||
              fairwayMapsToDelete.length > 0 ||
              fairwayEntranceMapsToDelete.length > 0
            ) {
              await updateHarbor({
                harbor_id: formData.id,
                data: payload,
              }).unwrap();
            }
            navigate('/habor-management');
          } else {
            enqueueSnackbar(
              intl.formatMessage({ id: 'HARBOR.NO_CHANGES_TO_UPDATE' })
            );
            navigate('/habor-management');
          }
        } else {
          const harborResponse = await createHarbor(payload).unwrap();

          const mediaArray = Array.isArray(formData.media)
            ? formData.media
            : [];
          if (mediaArray.length > 0) {
            const validMediaFiles = mediaArray.filter(
              (item) => item instanceof File
            ) as File[];
            if (validMediaFiles.length > 0) {
              const mediaPayload: MediaPayload = {
                id: harborResponse.id,
                media: validMediaFiles,
              };
              await createMediaHarborId(mediaPayload).unwrap();
            }
          }
          navigate('/habor-management');
        }
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: Record<string, string> = {};
        err.inner.forEach((error) => {
          if (error.path) {
            validationErrors[error.path] = error.message;
          }
        });
        setErrors(validationErrors);
        enqueueSnackbar(intl.formatMessage({ id: 'HARBOR.FORM.ERRORS' }), {
          variant: 'error',
        });
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message ||
          intl.formatMessage({ id: 'COMMON.AN_ERROR_OCCURRED' });
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleCancel = (): void => {
    navigate('/habor-management');
  };

  return (
    <Fragment key="harbor-form">
      <PageNavbar />
      <div className="container-fixed harbor-form responsive-form min-h-0">
        <div className="form-header flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 contrast:!text-white">
              {mode === 'create'
                ? intl.formatMessage({ id: 'HARBOR.FORM.CREATE.TITLE' })
                : intl.formatMessage({ id: 'HARBOR.FORM.EDIT.TITLE' })}
            </h1>
            <p className="text-sm text-gray-600 mt-1 contrast:!text-white">
              {mode === 'create'
                ? intl.formatMessage({ id: 'HARBOR.FORM.CREATE.DESCRIPTION' })
                : intl.formatMessage({ id: 'HARBOR.FORM.EDIT.DESCRIPTION' })}
            </p>
          </div>
          <div className="form-actions flex items-center gap-3">
            <div className="relative">
              <label htmlFor="language-select" className="sr-only">
                {intl.formatMessage({ id: 'HARBOR.FORM.SELECT_LANGUAGE' })}
              </label>
              <select
                id="language-select"
                className="select select-bordered pl-8 pr-8 h-10 leading-normal"
                value={currentLanguage.code}
                onChange={(e) => {
                  const selectedLang = I18N_LANGUAGES.find(
                    (lang) => lang.code === e.target.value
                  );
                  if (selectedLang) {
                    changeLanguage(selectedLang);
                  }
                }}
              >
                {I18N_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.code === 'en'
                      ? 'English'
                      : lang.code === 'de'
                        ? 'German'
                        : lang.code === 'sv'
                          ? 'Swedish'
                          : lang.code === 'fi'
                            ? 'Finnish'
                            : lang.label}
                  </option>
                ))}
              </select>
              <img
                src="/media/flags/globe.svg"
                alt="Globe"
                className="absolute dark:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none contrast:!brightness-0 contrast:!invert"
                aria-hidden="true"
              />
            </div>
            <button
              type="button"
              className="btn btn-sm btn-light flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 h-10 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
              onClick={handleCancel}
              aria-label={
                mode === 'create'
                  ? intl.formatMessage({ id: 'HARBOR.FORM.CANCEL.CREATE.ARIA' })
                  : intl.formatMessage({ id: 'HARBOR.FORM.CANCEL.EDIT.ARIA' })
              }
            >
              <KeenIcon type="filled" icon="cross" className="text-gray-500 " />
              {intl.formatMessage({ id: 'HARBOR.FORM.CANCEL' })}
            </button>
          </div>
        </div>
        <div className="space-y-8">
          {/* Logo and Fairway Map */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <MediaUpload
              key="logo"
              field="logo"
              media={formData.logo}
              setFormData={setFormData}
              errors={Object.fromEntries(
                Object.entries(errors).map(([key, value]) => [
                  key,
                  Array.isArray(value) ? value[0] : value,
                ])
              )}
              setErrors={(newErrors) => {
                setErrors((prev) => ({
                  ...prev,
                  ...Object.fromEntries(
                    Object.entries(newErrors).map(([key, value]) => [
                      key,
                      Array.isArray(value) ? value[0] : value,
                    ])
                  ),
                }));
              }}
              disabled={isCreating || isUpdating || isUploadingMedia}
              allowedTypes={['image/jpeg', 'image/jpg', 'image/png']}
              maxFiles={1}
              previewMode={true}
            />
            <MediaUpload
              key="fairway_map"
              field="fairway_map"
              media={formData.fairway_map}
              media_captions={formData.fairway_map_captions}
              setFormData={setFormData}
              errors={Object.fromEntries(
                Object.entries(errors).map(([key, value]) => [
                  key,
                  Array.isArray(value) ? value[0] : value,
                ])
              )}
              setErrors={(newErrors) => {
                setErrors((prev) => ({
                  ...prev,
                  ...Object.fromEntries(
                    Object.entries(newErrors).map(([key, value]) => [
                      key,
                      Array.isArray(value) ? value[0] : value,
                    ])
                  ),
                }));
              }}
              disabled={isCreating || isUpdating || isUploadingMedia}
              allowedTypes={[
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/svg+xml',
              ]}
              maxFiles={5}
              previewMode={true}
            />
            <MediaUpload
              key="fairway_entrance_map"
              field="fairway_entrance_map"
              media={formData.fairway_entrance_map}
              media_captions={formData.fairway_entrance_map_captions}
              setFormData={setFormData}
              errors={Object.fromEntries(
                Object.entries(errors).map(([key, value]) => [
                  key,
                  Array.isArray(value) ? value[0] : value,
                ])
              )}
              setErrors={(newErrors) => {
                setErrors((prev) => ({
                  ...prev,
                  ...Object.fromEntries(
                    Object.entries(newErrors).map(([key, value]) => [
                      key,
                      Array.isArray(value) ? value[0] : value,
                    ])
                  ),
                }));
              }}
              disabled={isCreating || isUpdating || isUploadingMedia}
              allowedTypes={[
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/svg+xml',
              ]}
              maxFiles={5}
              previewMode={true}
            />
            <MediaUpload
              key="berth_image"
              field="berth_image"
              media={
                formData.berth_image != null ? formData.berth_image : undefined
              }
              media_captions={formData.berth_image_captions ?? []}
              setFormData={setFormData}
              errors={Object.fromEntries(
                Object.entries(errors).map(([key, value]) => [
                  key,
                  Array.isArray(value) ? value[0] : value,
                ])
              )}
              setErrors={(newErrors) => {
                setErrors((prev) => ({
                  ...prev,
                  ...Object.fromEntries(
                    Object.entries(newErrors).map(([key, value]) => [
                      key,
                      Array.isArray(value) ? value[0] : value,
                    ])
                  ),
                }));
              }}
              disabled={isCreating || isUpdating || isUploadingMedia}
              allowedTypes={[
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/svg+xml',
              ]}
              maxFiles={1}
              previewMode={true}
            />
            <div className="mb-6">
              <label
                htmlFor="terms-pdf-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({
                  id: 'HARBOR.FORM.TERMS_PDF',
                })}
                <span
                  data-tooltip-id="termsTooltip"
                  data-tooltip-content={intl.formatMessage({
                    id: 'HARBOR.FORM.TERMS_PDF_GUIDELINES',
                    defaultMessage: 'Allowed format: .pdf. Max size: 20MB',
                  })}
                  className="ml-1 inline-flex"
                >
                  <KeenIcon
                    type="filled"
                    icon="information"
                    className="text-gray-500 cursor-pointer contrast:!text-white"
                  />
                </span>
                <Tooltip id="termsTooltip" place="top" className="!max-w-xs" />
              </label>

              <input
                id="terms-pdf-input"
                type="file"
                accept=".pdf"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.size > 20 * 1024 * 1024) {
                      setErrors((prev) => ({
                        ...prev,
                        terms_pdf: intl.formatMessage(
                          { id: 'VALIDATION.FILE_SIZE_TERMS' },
                          { max: 20 }
                        ),
                      }));
                      return;
                    }
                    setFormData((prev) => ({ ...prev, terms_pdf: file }));
                    if (errors.terms_pdf) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.terms_pdf;
                        return newErrors;
                      });
                    }
                  }
                }}
                disabled={isCreating || isUpdating || isUploadingMedia}
              />

              <div className="flex flex-wrap gap-2 mb-2">
                {(formData.terms_pdf instanceof File ||
                  (typeof formData.terms_pdf === 'string' &&
                    formData.terms_pdf)) && (
                  <div className="relative w-24 h-24 bg-gray-100 rounded-lg border border-gray-200 flex flex-col items-center justify-center p-2 text-center">
                    <KeenIcon
                      icon="document"
                      className="text-3xl text-gray-400 mb-1"
                    />
                    <span className="text-[10px] text-gray-600 contrast:text-white truncate w-full px-1">
                      {formData.terms_pdf instanceof File
                        ? formData.terms_pdf.name
                        : formData.terms_pdf.split('/').pop()}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, terms_pdf: null }))
                      }
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                      aria-label="Remove PDF"
                    >
                      <KeenIcon icon="cross" className="text-xs" />
                    </button>
                  </div>
                )}
              </div>

              {!isCreating && !isUpdating && !isUploadingMedia && (
                <div className="relative inline-block">
                  <button
                    type="button"
                    onClick={() =>
                      document.getElementById('terms-pdf-input')?.click()
                    }
                    className="py-2 px-4 rounded-lg border-0 text-sm font-semibold bg-blue-50 dark:bg-blue-900 contrast:bg-blue-900 text-blue-700 dark:text-blue-200 contrast:text-blue-100 hover:bg-blue-100 dark:hover:bg-blue-800 contrast:hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {intl.formatMessage({ id: 'HARBOR.FORM.SELECT_FILE' })}
                  </button>
                </div>
              )}
              {errors.terms_pdf && (
                <p className="text-red-500 text-xs mt-1" role="alert">
                  {errors.terms_pdf as string}
                </p>
              )}
            </div>
          </div>

          {/* Name and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white">
                {intl.formatMessage({ id: 'COMMON.NAME' })}
              </span>
              <input
                id="name-input"
                type="text"
                name="name"
                value={formData.name || ''}
                ref={nameInputRef}
                onChange={handleInputChange}
                onKeyDown={preventLeadingWhitespace}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.name
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'name-error' : undefined}
                aria-label={intl.formatMessage({ id: 'COMMON.NAME' })}
                autoComplete="organization"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_HARBOUR_NAME_PLACEHOLDER',
                })}
              />
              {errors.name && (
                <p
                  id="name-error"
                  className="text-red-500 text-xs mt-1 contrast:!text-[#FF8B33]"
                  role="alert"
                >
                  {errors.name}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="status-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.STATUS' })}
              </label>
              {!isStatusEditable ? (
                <input
                  id="status-input"
                  type="text"
                  value={getHarborStatusLabel(formData.status)}
                  disabled={true}
                  className={clsx(
                    'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                    'bg-gray-100 cursor-not-allowed',
                    errors.status ? 'border-red-500' : 'border-gray-300'
                  )}
                  aria-invalid={!!errors.status}
                  aria-describedby={errors.status ? 'status-error' : undefined}
                  aria-label={intl.formatMessage({ id: 'COMMON.STATUS' })}
                />
              ) : (
                <select
                  id="status-input"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                  disabled={isCreating || isUpdating || isUploadingMedia}
                  className={clsx(
                    'w-full bg-light-active h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                    errors.status ? 'border-red-500' : 'border-gray-300',
                    isCreating || isUpdating || isUploadingMedia
                      ? 'bg-gray-100 cursor-not-allowed'
                      : ''
                  )}
                  aria-invalid={!!errors.status}
                  aria-describedby={errors.status ? 'status-error' : undefined}
                  aria-label={intl.formatMessage({ id: 'COMMON.STATUS' })}
                >
                  <option value={1}>
                    {intl.formatMessage({ id: 'COMMON.ACTIVE' })}
                  </option>
                  <option value={0}>
                    {intl.formatMessage({ id: 'COMMON.INACTIVE' })}
                  </option>
                  <option value={4}>
                    {intl.formatMessage({ id: 'COMMON.UNDER_RENOVATION' })}
                  </option>
                </select>
              )}
              {errors.status && (
                <p
                  id="status-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.status}
                </p>
              )}
            </div>
            {/* Inactive period dates - only show when status is inactive */}
            {Number(formData.status) === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="inactive-start-date"
                    className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
                  >
                    {intl.formatMessage({ id: 'HARBOR.INACTIVE_START_DATE' })}
                  </label>
                  <input
                    type="date"
                    id="inactive-start-date"
                    value={inactiveStartDate}
                    onChange={(e) => setInactiveStartDate(e.target.value)}
                    onKeyDown={(e) => e.preventDefault()}
                    className="w-full input h-11 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    aria-label={intl.formatMessage({
                      id: 'HARBOR.INACTIVE_START_DATE',
                    })}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label
                    htmlFor="inactive-end-date"
                    className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
                  >
                    {intl.formatMessage({ id: 'HARBOR.INACTIVE_END_DATE' })}
                  </label>
                  <input
                    type="date"
                    id="inactive-end-date"
                    value={inactiveEndDate}
                    onChange={(e) => setInactiveEndDate(e.target.value)}
                    onKeyDown={(e) => e.preventDefault()} // Prevent typing
                    min={inactiveStartDate}
                    className="w-full input h-11 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    aria-label={intl.formatMessage({
                      id: 'HARBOR.INACTIVE_END_DATE',
                    })}
                    autoComplete="off"
                  />
                </div>
              </div>
            ) : null}
          </div>

          {/* Category and Marina */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="category-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.CATEGORY' })}
              </label>
              <select
                id="category-input"
                name="category"
                value={formData.category ?? ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full bg-light-active h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.category
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.category}
                aria-describedby={
                  errors.category ? 'category-error' : undefined
                }
                aria-label={intl.formatMessage({ id: 'COMMON.CATEGORY' })}
              >
                <option value="" disabled>
                  {intl.formatMessage({ id: 'COMMON.SELECT_CATEGORY' })}
                </option>
                {resolvedHarborCategoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p
                  id="category-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.category}
                </p>
              )}
            </div>
            <div>
              <span
                id="marina-label"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.MARINA' })}
              </span>
              <div
                id="marina-select"
                className="relative"
                ref={marinaDropdownRef}
              >
                <div
                  id="marina-id"
                  className={clsx(
                    'w-full h-11 px-4 py-2 border rounded-lg flex items-center justify-between cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                    isMarinaDropdownOpen &&
                      !isUsersLoading &&
                      'border-blue-500',
                    errors.marina_id
                      ? 'border-red-500  contrast:border-[#FF8B33]'
                      : 'border-gray-300 contrast:border-gray-300',
                    (isUsersLoading ||
                      isCreating ||
                      isUpdating ||
                      isUploadingMedia ||
                      isCurrentUserMarina) &&
                      'bg-gray-100 cursor-not-allowed'
                  )}
                  onClick={
                    isCurrentUserMarina ? undefined : handleMarinaDropdownToggle
                  }
                  onKeyDown={(e) => {
                    if (isCurrentUserMarina) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleMarinaDropdownToggle();
                    } else if (e.key === 'Escape' && isMarinaDropdownOpen) {
                      setIsMarinaDropdownOpen(false);
                    } else if (e.key === 'ArrowDown' && !isMarinaDropdownOpen) {
                      e.preventDefault();
                      setIsMarinaDropdownOpen(true);
                      setTimeout(() => {
                        // Focus the search input when dropdown opens
                        const searchInput =
                          marinaOptionsContainerRef.current?.querySelector(
                            'input'
                          ) as HTMLElement;
                        if (searchInput) {
                          searchInput.focus();
                        } else {
                          const firstOption =
                            marinaOptionsContainerRef.current?.querySelector(
                              '[role="option"]'
                            ) as HTMLElement;
                          firstOption?.focus();
                        }
                      }, 0);
                    }
                  }}
                  tabIndex={0}
                  aria-label={intl.formatMessage({
                    id: 'HARBOR.SELECT_MARINA',
                  })}
                  aria-labelledby="marina-label"
                  aria-invalid={!!errors.marina_id}
                  aria-describedby={
                    errors.marina_id ? 'marina-error' : undefined
                  }
                  aria-haspopup="listbox"
                  aria-expanded={isMarinaDropdownOpen}
                  aria-controls="marina-options-listbox"
                  role="combobox"
                >
                  <div className="truncate">
                    {(() => {
                      if (
                        formData.manager?.name &&
                        formData.manager.name.trim() !== ''
                      ) {
                        return formData.manager.name;
                      }
                      if (formData.manager?.id) {
                        const found = marinaOptions.find(
                          (m) => m.id === formData.manager?.id
                        );
                        if (found) {
                          return (
                            found.name ||
                            found.email ||
                            intl.formatMessage({ id: 'HARBOR.SELECT_MARINA' })
                          );
                        }
                      }
                      return isUsersLoading
                        ? intl.formatMessage({ id: 'HARBOR.LOADING_MARINAS' })
                        : intl.formatMessage({ id: 'HARBOR.SELECT_MARINA' });
                    })()}
                  </div>
                  <div className="flex items-center">
                    <svg
                      className={clsx(
                        'w-4 h-4 text-gray-400 transition-transform duration-200',
                        isMarinaDropdownOpen && 'transform rotate-180'
                      )}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
                {isMarinaDropdownOpen &&
                  !isUsersLoading &&
                  !isCurrentUserMarina && (
                    <div
                      className="absolute z-50 w-full mt-1 bg-gray-100 border border-gray-300 rounded-lg shadow-lg"
                      ref={marinaOptionsContainerRef}
                    >
                      <div className="bg-gray-100 p-2 border-b border-gray-300 rounded-t-lg">
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={intl.formatMessage({
                            id: 'COMMON.SEARCH',
                          })}
                          value={marinaSearch.searchTerm}
                          onChange={marinaSearch.handleSearch}
                          onKeyDown={(e) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                              e.stopPropagation();
                            } else if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              const firstOption =
                                marinaOptionsContainerRef.current?.querySelector(
                                  '[role="option"]'
                                ) as HTMLElement;
                              firstOption?.focus();
                            } else if (e.key === 'Escape') {
                              setIsMarinaDropdownOpen(false);
                              const combobox = marinaOptionsContainerRef.current
                                ?.closest('.relative')
                                ?.querySelector(
                                  '[role="combobox"]'
                                ) as HTMLElement;
                              combobox?.focus();
                            }
                          }}
                        />
                      </div>
                      {marinaOptions.length > 0 ? (
                        <div
                          id="marina-options-listbox"
                          className="max-h-48 overflow-y-auto"
                          role="listbox"
                          aria-labelledby="marina-label"
                        >
                        {marinaOptions.map((marina, index) => (
                          <div
                            key={marina.id}
                            className={clsx(
                              'px-4 py-2 text-sm cursor-pointer hover:bg-gray-200 focus:bg-gray-200 focus:outline-none',
                              formData.marina?.id === marina.id &&
                                'bg-blue-50 dark:bg-gray-400 contrast:bg-gray-400'
                            )}
                            onClick={() => handleMarinaSelect(marina)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleMarinaSelect(marina);
                              } else if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                const nextIndex =
                                  (index + 1) % marinaOptions.length;
                                const nextElement = e.currentTarget
                                  .parentElement?.children[
                                  nextIndex
                                ] as HTMLElement;
                                nextElement?.focus();
                              } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                if (index === 0) {
                                  const searchInput =
                                    marinaOptionsContainerRef.current?.querySelector(
                                      'input'
                                    ) as HTMLElement;
                                  searchInput?.focus();
                                } else {
                                  const prevIndex = index - 1;
                                  const prevElement = e.currentTarget
                                    .parentElement?.children[
                                    prevIndex
                                  ] as HTMLElement;
                                  prevElement?.focus();
                                }
                              } else if (e.key === 'Escape') {
                                setIsMarinaDropdownOpen(false);
                                // Focus back to the combobox
                                const combobox = e.currentTarget
                                  .closest('.relative')
                                  ?.querySelector(
                                    '[role="combobox"]'
                                  ) as HTMLElement;
                                combobox?.focus();
                              }
                            }}
                            tabIndex={0}
                            role="option"
                            aria-selected={formData.marina?.id === marina.id}
                          >
                            {marina.email}
                            {marina.name ? ` (${marina.name})` : ''}
                          </div>
                        ))}
                        </div>
                      ) : (
                        <div className="px-4 py-2 text-sm text-gray-500">
                          {intl.formatMessage({ id: 'COMMON.NO_DATA_FOUND' })}
                        </div>
                      )}
                    </div>
                  )}
              </div>
              {errors.marina_id && (
                <p
                  id="marina-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.marina_id}
                </p>
              )}
            </div>
          </div>

          {/* Yacht Clubs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="club_id"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.YACHT_CLUB' })}
              </label>
              {/* <Select
                isMulti
                id="yacht_club_ids"
                options={yachtClubOptions}
                value={yachtClubOptions.filter((option) =>
                  formData.yachtClubIds?.includes(option.id),
                )}
                onChange={handleYachtClubChange}
                placeholder="Select Yacht Clubs"
                className="basic-multi-select"
                classNamePrefix="select"
                noOptionsMessage={() => "No yacht clubs found"}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "0.5rem",
                    borderColor: errors.yachtClubIds ? "#ef4444" : "#d1d5db",
                    minHeight: "2.75rem",
                    fontSize: "0.875rem",
                    lineHeight: "1.25rem",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: errors.yachtClubIds ? "#ef4444" : "#93c5fd",
                    },
                  }),
                }}
              /> */}
              <input
                type="text"
                id="club_id"
                name="club_id"
                placeholder={
                  formData.clubId
                    ? formData.clubId
                    : intl.formatMessage({ id: 'HARBOR.NO_YACHT_CLUB_ADDED' })
                }
                value={formData.club?.name || ''}
                onChange={(e) => handleInputBlur('clubId', e.target.value)}
                disabled={true}
                autoComplete="off"
                className="w-full bg-light-active px-4 py-2 border rounded-lg focus:ring-primary focus:border-primary"
              />
              {errors.clubId && (
                <p
                  id="club_id-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.clubId}
                </p>
              )}
            </div>
            <div>
              <span
                id="region-ids-label"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.REGIONS' })}
              </span>
              <Select
                inputId="region-ids"
                isMulti
                options={regionOptions}
                value={regionOptions.filter((option) =>
                  formData.regionIds?.includes(option.value)
                )}
                onChange={handleRegionChange}
                isDisabled={
                  isCreating ||
                  isUpdating ||
                  isUploadingMedia ||
                  isRegionsLoading
                }
                className={clsx(
                  'basic-multi-select',
                  errors.regionIds
                    ? 'border-red-500 contrast:!border-[#FF8B33]'
                    : 'contrast:!border-gray-600'
                )}
                classNamePrefix="react-select"
                placeholder={intl.formatMessage({
                  id: 'COMMON.SELECT_REGIONS',
                })}
                aria-labelledby="region-ids-label"
                aria-label="Select regions"
              />
              {errors.regionIds && (
                <p
                  id="region-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.regionIds}
                </p>
              )}
            </div>
          </div>

          {/* Mooring */}
          <div className="grid grid-cols-3 gap-6">
            <div>
              <span
                id="harbor-type-label"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'HARBOR.HARBOR_TYPE' })}
              </span>
              <Select
                inputId="harbor-type-input"
                name="harbor_type"
                value={
                  harborTypeOptions.find(
                    (option) => option.value === formData.harbor_type
                  ) || null
                }
                onChange={(selectedOption) => {
                  const value = selectedOption ? selectedOption.value : '';
                  handleInputChange({
                    target: {
                      name: 'harbor_type',
                      value,
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                  handleInputBlur('harbor_type', value);
                }}
                onBlur={() =>
                  handleInputBlur('harbor_type', formData.harbor_type || '')
                }
                isDisabled={isCreating || isUpdating || isUploadingMedia}
                options={harborTypeOptions}
                placeholder={intl.formatMessage({
                  id: 'HARBOR.SELECT_HARBOR_TYPE',
                })}
                className={clsx(
                  'react-select-container',
                  errors.harbor_type && 'react-select-container--error'
                )}
                classNamePrefix="react-select"
                aria-invalid={!!errors.harbor_type}
                aria-describedby={
                  errors.harbor_type ? 'harbor-type-error' : undefined
                }
                aria-labelledby="harbor-type-label"
                aria-label={intl.formatMessage({ id: 'HARBOR.HARBOR_TYPE' })}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '44px',
                    height: '44px',
                    borderColor: errors.harbor_type
                      ? '#EF4444'
                      : state.isFocused
                        ? '#3B82F6'
                        : '#D1D5DB',
                    '&:hover': {
                      borderColor: errors.harbor_type
                        ? '#EF4444'
                        : state.isFocused
                          ? '#3B82F6'
                          : '#9CA3AF',
                    },
                    boxShadow: state.isFocused
                      ? '0 0 0 2px rgba(59, 130, 246, 0.5)'
                      : 'none',
                    backgroundColor:
                      isCreating || isUpdating || isUploadingMedia
                        ? '#F3F4F6'
                        : 'white',
                    cursor:
                      isCreating || isUpdating || isUploadingMedia
                        ? 'not-allowed'
                        : 'default',
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    height: '100%',
                    padding: '0 12px',
                  }),
                  input: (base) => ({
                    ...base,
                    margin: 0,
                    padding: 0,
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#9CA3AF',
                    margin: 0,
                  }),
                }}
              />
              {errors.harbor_type && (
                <p
                  id="harbor-type-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.harbor_type}
                </p>
              )}
            </div>
            <div>
              <span
                id="primary-language-spoken-label"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.PRIMARY_LANGUAGE_SPOKEN' })}
              </span>
              <Select
                inputId="primary-language-spoken-input"
                name="primary_language_spoken"
                value={LANGUAGE_OPTIONS.find(
                  (option) => option.value === formData.primary_language_spoken
                )}
                onChange={(option) =>
                  setFormData({
                    ...formData,
                    primary_language_spoken: option?.value || '',
                  })
                }
                options={LANGUAGE_OPTIONS}
                placeholder={intl.formatMessage({
                  id: 'COMMON.SELECT_LANGUAGE',
                })}
                className="react-select-container"
                classNamePrefix="react-select"
                isDisabled={isCreating || isUpdating || isUploadingMedia}
                aria-labelledby="primary-language-spoken-label"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '0.5rem',
                    borderColor: errors.primary_language_spoken
                      ? '#ef4444'
                      : '#d1d5db',
                    minHeight: '2.75rem',
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    boxShadow: 'none',
                    backgroundColor:
                      isCreating || isUpdating || isUploadingMedia
                        ? '#f3f4f6'
                        : '#ffffff',
                    '&:hover': {
                      borderColor: errors.primary_language_spoken
                        ? '#ef4444'
                        : '#93c5fd',
                    },
                  }),
                }}
              />
              {errors.primary_language_spoken && (
                <p
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.primary_language_spoken}
                </p>
              )}
            </div>
            <div>
              <span
                id="coastal-area-label"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.COASTAL_AREA' })}
              </span>
              <Select
                inputId="coastal-area"
                name="coastal_area"
                value={
                  coastalAreaData?.find(
                    (area) => area.name === formData.coastal_area
                  ) || null
                }
                onChange={(selectedOption) => {
                  const value = selectedOption
                    ? (selectedOption as CoastalAreaResponse).name
                    : '';
                  handleInputChange({
                    target: {
                      name: 'coastal_area',
                      value,
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                  handleInputBlur('coastal_area', value);
                }}
                onBlur={() =>
                  handleInputBlur('coastal_area', formData.coastal_area || '')
                }
                isDisabled={
                  isCreating ||
                  isUpdating ||
                  isUploadingMedia ||
                  !coastalAreaData
                }
                options={coastalAreaData}
                getOptionLabel={(option: CoastalAreaResponse) => option.label}
                getOptionValue={(option: CoastalAreaResponse) => option.name}
                placeholder={intl.formatMessage({
                  id: 'COMMON.SELECT_COASTAL_AREA',
                })}
                noOptionsMessage={() =>
                  coastalAreaData
                    ? intl.formatMessage({ id: 'COMMON.NO_OPTIONS_FOUND' })
                    : intl.formatMessage({ id: 'COMMON.LOADING_COASTAL_AREAS' })
                }
                className={clsx(
                  'react-select-container',
                  errors.coastal_area && 'react-select-container--error'
                )}
                classNamePrefix="react-select"
                aria-invalid={!!errors.coastal_area}
                aria-describedby={
                  errors.coastal_area ? 'coastal-area-error' : undefined
                }
                aria-labelledby="coastal-area-label"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '44px',
                    height: '44px',
                    borderColor: errors.coastal_area
                      ? '#EF4444'
                      : state.isFocused
                        ? '#3B82F6'
                        : '#D1D5DB',
                    '&:hover': {
                      borderColor: errors.coastal_area
                        ? '#EF4444'
                        : state.isFocused
                          ? '#3B82F6'
                          : '#9CA3AF',
                    },
                    boxShadow: state.isFocused
                      ? '0 0 0 2px rgba(59, 130, 246, 0.5)'
                      : 'none',
                    backgroundColor:
                      isCreating || isUpdating || isUploadingMedia
                        ? '#F3F4F6'
                        : 'white',
                    cursor:
                      isCreating || isUpdating || isUploadingMedia
                        ? 'not-allowed'
                        : 'default',
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    height: '100%',
                    padding: '0 12px',
                  }),
                  input: (base) => ({
                    ...base,
                    margin: 0,
                    padding: 0,
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#9CA3AF',
                    margin: 0,
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 20,
                  }),
                }}
              />
              {errors.coastal_area && (
                <p
                  id="coastal-area-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.coastal_area}
                </p>
              )}
            </div>
          </div>

          {/* Booking Settings Row 1: Require Approval + Berth Conflict */}
          <div className="flex flex-col md:flex-row md:items-start md:space-x-10 gap-4">
            {/* Require Booking Approval */}
            <div className="flex flex-col">
              <div className="flex items-start flex-col gap-[10px]">
                <span
                  id="label-booking-approval"
                  className="block text-sm font-medium text-gray-700 contrast:!text-white"
                >
                  {intl.formatMessage({
                    id: 'COMMON.REQUIRE_APPROVAL_FOR_BOOKING_REQUESTS',
                  })}
                  :
                  <span className="block text-xs font-normal text-gray-500 mt-1">
                    {intl.formatMessage({
                      id: 'COMMON.REQUIRE_APPROVAL_GUEST_BOOKING_NOTE',
                    })}
                  </span>
                </span>
                <div
                  className="flex items-center space-x-4"
                  role="radiogroup"
                  aria-labelledby="label-booking-approval"
                >
                  <label
                    htmlFor="booking-approval-yes"
                    className="inline-flex items-center cursor-pointer"
                  >
                    <input
                      id="booking-approval-yes"
                      type="radio"
                      name="send_booking_to_admin"
                      value="true"
                      checked={formData.send_booking_to_admin === true}
                      onChange={handleInputChange}
                      disabled={isCreating || isUpdating || isUploadingMedia}
                      className="h-4 w-4 text-blue-500 border-gray-200 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">
                      {intl.formatMessage({ id: 'COMMON.YES' })}
                    </span>
                  </label>
                  <label
                    htmlFor="booking-approval-no"
                    className="inline-flex items-center cursor-pointer"
                  >
                    <input
                      id="booking-approval-no"
                      type="radio"
                      name="send_booking_to_admin"
                      value="false"
                      checked={formData.send_booking_to_admin === false}
                      onChange={handleInputChange}
                      disabled={isCreating || isUpdating || isUploadingMedia}
                      className="h-4 w-4 text-blue-500 border-gray-200 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">
                      {intl.formatMessage({ id: 'COMMON.NO' })}
                    </span>
                  </label>
                </div>
              </div>
              {errors.send_booking_to_admin && (
                <p
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.send_booking_to_admin}
                </p>
              )}
            </div>

            <div className="flex flex-col">
              <label
                htmlFor="mediation-response-deadline-hours"
                className="block text-sm font-medium text-gray-700 contrast:!text-white mb-1"
              >
                {intl.formatMessage({
                  id: 'HARBOR.FORM.BERTH_CONFLICT_RESPONSE_DEADLINE_HOURS',
                  defaultMessage: 'Berth conflict response deadline (hours)',
                })}
              </label>
              <input
                id="mediation-response-deadline-hours"
                type="number"
                name="mediation_response_deadline_hours"
                min={1}
                max={168}
                value={formData.mediation_response_deadline_hours ?? 48}
                onChange={handleInputChange}
                autoComplete="off"
                disabled={isCreating || isUpdating || isUploadingMedia}
                className="w-full max-w-xs border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                {intl.formatMessage({
                  id: 'HARBOR.FORM.BERTH_CONFLICT_RESPONSE_DEADLINE_HOURS_DESCRIPTION',
                  defaultMessage:
                    'Hours Boater B has to respond to a proposed berth switch (1–168). Default 48.',
                })}
              </p>
            </div>
          </div>

          {/* Booking Settings Row 2: Booking Enabled + Booking Type */}
          <div className="flex flex-col md:flex-row md:items-start md:space-x-10 gap-4">
            {/* Booking Enabled */}
            <div className="flex flex-col">
              <div className="flex items-start flex-col gap-[10px]">
                <span
                  id="label-booking-enabled"
                  className="block text-sm font-medium text-gray-700 contrast:!text-white"
                >
                  {intl.formatMessage({
                    id: 'COMMON.BOOKING_ENABLED',
                    defaultMessage: 'Booking Enabled',
                  })}
                  :
                </span>
                <div
                  className="flex items-center space-x-4"
                  role="radiogroup"
                  aria-labelledby="label-booking-enabled"
                >
                  <label
                    htmlFor="booking-enabled-yes"
                    className="inline-flex items-center cursor-pointer"
                  >
                    <input
                      id="booking-enabled-yes"
                      type="radio"
                      name="booking_enabled"
                      value="true"
                      checked={formData.booking_enabled === true}
                      onChange={handleInputChange}
                      disabled={isCreating || isUpdating || isUploadingMedia}
                      className="h-4 w-4 text-blue-500 border-gray-200 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">
                      {intl.formatMessage({ id: 'COMMON.YES' })}
                    </span>
                  </label>
                  <label
                    htmlFor="booking-enabled-no"
                    className="inline-flex items-center cursor-pointer"
                  >
                    <input
                      id="booking-enabled-no"
                      type="radio"
                      name="booking_enabled"
                      value="false"
                      checked={formData.booking_enabled === false}
                      onChange={handleInputChange}
                      disabled={isCreating || isUpdating || isUploadingMedia}
                      className="h-4 w-4 text-blue-500 border-gray-200 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">
                      {intl.formatMessage({ id: 'COMMON.NO' })}
                    </span>
                  </label>
                </div>
              </div>
              {errors.booking_enabled && (
                <p
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.booking_enabled}
                </p>
              )}
            </div>

            {/* Booking Type */}
            <div className="flex flex-col">
              <div className="flex items-start flex-col gap-[10px]">
                <span
                  id="label-booking-type"
                  className="block text-sm font-medium text-gray-700 contrast:!text-white"
                >
                  {intl.formatMessage({
                    id: 'COMMON.BOOKING_TYPE',
                    defaultMessage: 'Booking Type',
                  })}
                  :
                </span>
                <div
                  className="flex items-center space-x-4"
                  role="radiogroup"
                  aria-labelledby="label-booking-type"
                >
                  <label
                    htmlFor="booking-type-reservation"
                    className="inline-flex items-center cursor-pointer whitespace-nowrap"
                  >
                    <input
                      id="booking-type-reservation"
                      type="radio"
                      name="booking_type"
                      value={HarborBookingType.RESERVATION}
                      checked={
                        formData.booking_type === HarborBookingType.RESERVATION
                      }
                      onChange={handleInputChange}
                      disabled={isCreating || isUpdating || isUploadingMedia}
                      className="h-4 w-4 text-blue-500 border-gray-200 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">
                      {intl.formatMessage({
                        id: 'COMMON.RESERVATION',
                        defaultMessage: 'Reservation',
                      })}
                    </span>
                  </label>
                  <label
                    htmlFor="booking-type-payment"
                    className="inline-flex items-center cursor-pointer whitespace-nowrap"
                  >
                    <input
                      id="booking-type-payment"
                      type="radio"
                      name="booking_type"
                      value={HarborBookingType.PAYMENT_ONLY}
                      checked={
                        formData.booking_type === HarborBookingType.PAYMENT_ONLY
                      }
                      onChange={handleInputChange}
                      disabled={isCreating || isUpdating || isUploadingMedia}
                      className="h-4 w-4 text-blue-500 border-gray-200 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">
                      {intl.formatMessage({
                        id: 'COMMON.PAYMENT_ONLY',
                        defaultMessage: 'Payment Only',
                      })}
                    </span>
                  </label>
                </div>
              </div>
              {errors.booking_type && (
                <p
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.booking_type as string}
                </p>
              )}
            </div>
          </div>

          {/* Paytrail Merchant ID */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="paytrail-merchant-id-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({
                  id: 'HARBOR.FORM.PAYTRAIL_MERCHANT_ID',
                  defaultMessage: 'Paytrail Merchant ID',
                })}
              </label>
              <input
                id="paytrail-merchant-id-input"
                type="text"
                name="paytrail_merchant_id"
                value={formData.paytrail_merchant_id || ''}
                onChange={handleInputChange}
                onKeyDown={preventLeadingWhitespace}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={`w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 contrast:border-gray-300 ${
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                }`}
                aria-label={intl.formatMessage({
                  id: 'HARBOR.FORM.PAYTRAIL_MERCHANT_ID',
                  defaultMessage: 'Paytrail Merchant ID',
                })}
                placeholder={intl.formatMessage({
                  id: 'HARBOR.FORM.PAYTRAIL_MERCHANT_ID_PLACEHOLDER',
                  defaultMessage: 'Enter Paytrail Merchant ID',
                })}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Social Media Links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="whatsapp-link-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.WHATSAPP_LINK' })}
              </label>
              <input
                id="whatsapp-link-input"
                type="text"
                name="whatsapp_link"
                value={formData.whatsapp_link || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.whatsapp_link
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-600',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.whatsapp_link}
                aria-describedby={
                  errors.whatsapp_link ? 'whatsapp-link-error' : undefined
                }
                aria-label={intl.formatMessage({ id: 'COMMON.WHATSAPP_LINK' })}
                autoComplete="url"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_WHATSAPP_LINK',
                })}
              />
              {errors.whatsapp_link && (
                <p
                  id="whatsapp-link-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.whatsapp_link}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="facebook-link-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.FACEBOOK_LINK' })}
              </label>
              <input
                id="facebook-link-input"
                type="text"
                name="facebook_link"
                value={formData.facebook_link || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.facebook_link
                    ? 'border-red-500  contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.facebook_link}
                aria-describedby={
                  errors.facebook_link ? 'facebook-link-error' : undefined
                }
                aria-label={intl.formatMessage({ id: 'COMMON.FACEBOOK_LINK' })}
                autoComplete="url"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_FACEBOOK_LINK',
                })}
              />
              {errors.facebook_link && (
                <p
                  id="facebook-link-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.facebook_link}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="instagram-link-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.INSTAGRAM_LINK' })}
              </label>
              <input
                id="instagram-link-input"
                type="text"
                name="instagram_link"
                value={formData.instagram_link || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.instagram_link
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.instagram_link}
                aria-describedby={
                  errors.instagram_link ? 'instagram-link-error' : undefined
                }
                aria-label={intl.formatMessage({ id: 'COMMON.INSTAGRAM_LINK' })}
                autoComplete="url"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_INSTAGRAM_LINK',
                })}
              />
              {errors.instagram_link && (
                <p
                  id="instagram-link-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.instagram_link}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="pdf-link-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({
                  id: 'COMMON.OFFICIAL_BROCHURE_PDF_LINK',
                })}
              </label>
              <input
                id="pdf-link-input"
                type="text"
                name="pdf_link"
                value={formData.pdf_link || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.pdf_link
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.pdf_link}
                aria-describedby={
                  errors.pdf_link ? 'pdf-link-error' : undefined
                }
                aria-label={intl.formatMessage({
                  id: 'COMMON.OFFICIAL_BROCHURE_PDF_LINK',
                })}
                autoComplete="url"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_PDF_LINK',
                })}
              />
              {errors.pdf_link && (
                <p
                  id="pdf-link-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.pdf_link}
                </p>
              )}
            </div>
          </div>

          {/* Location and Coordinates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white">
                {intl.formatMessage({ id: 'COMMON.LOCATION' })}
              </span>
              <input
                id="location-input"
                type="text"
                name="location"
                value={formData.location || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.location
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.location}
                aria-describedby={
                  errors.location ? 'location-error' : undefined
                }
                aria-label={intl.formatMessage({ id: 'COMMON.LOCATION' })}
                autoComplete="street-address"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_HARBOUR_LOCATION',
                })}
              />
              {errors.location && (
                <p
                  id="location-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.location}
                </p>
              )}
            </div>
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white">
                {intl.formatMessage({ id: 'COMMON.CITY' })}
              </span>
              <input
                id="city-input"
                type="text"
                name="city"
                value={formData.city || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-label={intl.formatMessage({ id: 'COMMON.CITY' })}
                autoComplete="address-level2"
                placeholder={intl.formatMessage({ id: 'COMMON.ENTER_CITY' })}
              />
            </div>
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white">
                {intl.formatMessage({ id: 'COMMON.LATITUDE' })}
              </span>
              <NumberInput
                id="latitude-input"
                name="latitude"
                allowNegative
                step="0.0001"
                min="-90"
                max="90"
                maxDecimals={100}
                value={formData.latitude ?? ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.latitude
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.latitude}
                aria-describedby={
                  errors.latitude ? 'latitude-error' : undefined
                }
                aria-label={intl.formatMessage({ id: 'COMMON.LATITUDE' })}
                autoComplete="off"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_LATITUDE',
                })}
              />
              {errors.latitude && (
                <p
                  id="latitude-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.latitude}
                </p>
              )}
            </div>
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white">
                {intl.formatMessage({ id: 'COMMON.LONGITUDE' })}
              </span>
              <NumberInput
                id="longitude-input"
                name="longitude"
                step="0.0001"
                min="-180"
                max="180"
                maxDecimals={100}
                value={formData.longitude ?? ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.longitude
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.longitude}
                aria-describedby={
                  errors.longitude ? 'longitude-error' : undefined
                }
                aria-label={intl.formatMessage({ id: 'COMMON.LONGITUDE' })}
                autoComplete="off"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_LONGITUDE',
                })}
              />
              {errors.longitude && (
                <p
                  id="longitude-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.longitude}
                </p>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label
                htmlFor="contact-email-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.CONTACT_EMAIL' })}
              </label>
              <input
                id="contact-email-input"
                type="email"
                name="contact_email"
                value={formData.contact_email || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.contact_email
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.contact_email}
                aria-describedby={
                  errors.contact_email ? 'contact-email-error' : undefined
                }
                aria-label={intl.formatMessage({ id: 'COMMON.CONTACT_EMAIL' })}
                autoComplete="email"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_CONTACT_EMAIL',
                })}
              />
              {errors.contact_email && (
                <p
                  id="contact-email-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.contact_email}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="contact-phone-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.CONTACT_PHONE' })}
              </label>
              <div className="flex gap-2">
                <div className="w-32 flex-shrink-0">
                  <CountryCodeSelect
                    disabled={isCreating || isUploadingMedia}
                    selected={formData.contact_phone_country_short_code || formData.country_short_code || ''}
                    setFieldValue={handleDialCodeChange}
                    hasError={!!errors.country_code}
                    intl={intl}
                  />
                </div>
                <div className="flex-1">
                  <input
                    id="contact-phone-input"
                    type="text"
                    name="contact_phone"
                    value={formData.contact_phone || ''}
                    onChange={handleInputChange}
                    onBlur={(e) =>
                      handleInputBlur(e.target.name, e.target.value)
                    }
                    disabled={isCreating || isUpdating || isUploadingMedia}
                    className={clsx(
                      'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                      errors.contact_phone
                        ? 'border-red-500 contrast:border-[#FF8B33]'
                        : 'border-gray-300 contrast:border-gray-300',
                      isCreating || isUpdating || isUploadingMedia
                        ? 'bg-gray-100 cursor-not-allowed'
                        : ''
                    )}
                    aria-invalid={!!errors.contact_phone}
                    aria-describedby={
                      errors.contact_phone ? 'contact-phone-error' : undefined
                    }
                    aria-label={intl.formatMessage({
                      id: 'COMMON.CONTACT_PHONE',
                    })}
                    autoComplete="tel"
                    placeholder={intl.formatMessage({
                      id: 'COMMON.ENTER_CONTACT_PHONE',
                    })}
                  />
                </div>
              </div>
              {errors.contact_phone && (
                <p
                  id="contact-phone-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.contact_phone}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="website-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.WEBSITE' })}
              </label>
              <input
                id="website-input"
                type="text"
                name="website_url"
                value={formData.website_url || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.website
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.website}
                aria-describedby={errors.website ? 'website-error' : undefined}
                aria-label={intl.formatMessage({ id: 'COMMON.WEBSITE' })}
                autoComplete="url"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_WEBSITE_URL',
                })}
              />
              {errors.website && (
                <p
                  id="website-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.website}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div>
              <label
                htmlFor="default-check-in-time-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'HARBOR.DEFAULT_CHECK_IN_TIME' })}
              </label>
              <input
                id="default-check-in-time-input"
                type="time"
                name="default_check_in_time"
                value={formData.default_check_in_time || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.default_check_in_time
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.default_check_in_time}
                aria-describedby={
                  errors.default_check_in_time
                    ? 'default-check-in-time-error'
                    : undefined
                }
                autoComplete="off"
              />
              {errors.default_check_in_time && (
                <p
                  id="default-check-in-time-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.default_check_in_time as string}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="default-check-out-time-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'HARBOR.DEFAULT_CHECK_OUT_TIME' })}
              </label>
              <input
                id="default-check-out-time-input"
                type="time"
                name="default_check_out_time"
                value={formData.default_check_out_time || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.default_check_out_time
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.default_check_out_time}
                aria-describedby={
                  errors.default_check_out_time
                    ? 'default-check-out-time-error'
                    : undefined
                }
                autoComplete="off"
              />
              {errors.default_check_out_time && (
                <p
                  id="default-check-out-time-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.default_check_out_time as string}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="check-in-window-start-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'HARBOR.CHECK_IN_WINDOW_START' })}
              </label>
              <input
                id="check-in-window-start-input"
                type="time"
                name="check_in_window_start"
                value={formData.check_in_window_start || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.check_in_window_start
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.check_in_window_start}
                autoComplete="off"
              />
              {errors.check_in_window_start && (
                <p
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.check_in_window_start as string}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="check-in-window-end-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'HARBOR.CHECK_IN_WINDOW_END' })}
              </label>
              <input
                id="check-in-window-end-input"
                type="time"
                name="check_in_window_end"
                value={formData.check_in_window_end || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.check_in_window_end
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.check_in_window_end}
                autoComplete="off"
              />
              {errors.check_in_window_end && (
                <p
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.check_in_window_end as string}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="check-out-window-start-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'HARBOR.CHECK_OUT_WINDOW_START' })}
              </label>
              <input
                id="check-out-window-start-input"
                type="time"
                name="check_out_window_start"
                value={formData.check_out_window_start || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.check_out_window_start
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.check_out_window_start}
                autoComplete="off"
              />
              {errors.check_out_window_start && (
                <p
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.check_out_window_start as string}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="check-out-window-end-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'HARBOR.CHECK_OUT_WINDOW_END' })}
              </label>
              <input
                id="check-out-window-end-input"
                type="time"
                name="check_out_window_end"
                value={formData.check_out_window_end || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.check_out_window_end
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.check_out_window_end}
                autoComplete="off"
              />
              {errors.check_out_window_end && (
                <p
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.check_out_window_end as string}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="timezone-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                Timezone (IANA identifier)
              </label>
              <select
                id="timezone-input"
                name="timezone"
                value={formData.timezone || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full bg-light-active h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.timezone
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.timezone}
                aria-describedby={
                  errors.timezone ? 'timezone-error' : undefined
                }
              >
                <option value="" disabled>
                  Select timezone
                </option>
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
              {errors.timezone && (
                <p
                  id="timezone-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.timezone as string}
                </p>
              )}
            </div>
          </div>

          {/* General Country */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
            <div>
              <label
                id="harbor-country-label"
                htmlFor="harbor-country-select"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.COUNTRY' })}
              </label>
              <div
                ref={countryDropdownRef}
                className="relative"
                onKeyDown={handleCountryDropdownKeyDown}
                onBlur={handleCountryDropdownBlur}
              >
                <div
                  ref={countryTriggerRef}
                  id="harbor-country-select"
                  role="combobox"
                  aria-expanded={isCountryDropdownOpen}
                  aria-haspopup="listbox"
                  aria-controls="harbor-country-listbox"
                  aria-autocomplete="list"
                  aria-activedescendant={countryActiveDescendantId}
                  aria-labelledby="harbor-country-label"
                  aria-invalid={!!errors.country_short_code}
                  aria-describedby={
                    errors.country_short_code
                      ? 'harbor-country-error'
                      : undefined
                  }
                  aria-disabled={isCountryFieldDisabled}
                  tabIndex={isCountryFieldDisabled ? -1 : 0}
                  className={clsx(
                    'input input-bordered w-full h-11 flex items-center justify-between cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500',
                    errors.country_short_code &&
                      'border-red-500 contrast:border-[#FF8B33] focus:ring-red-500',
                    isCountryFieldDisabled ? 'bg-gray-100 cursor-not-allowed' : ''
                  )}
                  onClick={() => {
                    if (isCountryFieldDisabled) return;
                    if (isCountryDropdownOpen) {
                      closeCountryDropdown();
                    } else {
                      openCountryDropdown('search');
                    }
                  }}
                >
                  <span className="truncate">
                    {countriesList.find(
                      (c) => c.code === formData.country_short_code
                    )?.name || 'Select country'}
                  </span>
                  {!isCountryFieldDisabled && (
                    <KeenIcon
                      icon={isCountryDropdownOpen ? 'up' : 'down'}
                      className="text-gray-400"
                    />
                  )}
                </div>

                {isCountryDropdownOpen && !isCountryFieldDisabled && (
                  <div className="absolute z-50 w-full mt-1 bg-gray-100 border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
                    <div className="p-2 border-b border-gray-200 bg-gray-200">
                      <div className="relative">
                        <KeenIcon
                          icon="magnifier"
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                        <input
                          ref={countrySearchInputRef}
                          type="text"
                          role="searchbox"
                          aria-autocomplete="list"
                          aria-controls="harbor-country-listbox"
                          aria-activedescendant={countryActiveDescendantId}
                          aria-label={intl.formatMessage({
                            id: 'COMMON.SEARCH',
                          })}
                          placeholder="Search countries..."
                          value={countrySearchTerm}
                          onChange={(e) => {
                            setCountrySearchTerm(e.target.value);
                            setCountryActiveIndex(0);
                          }}
                          className="input input-sm w-full pl-10 border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                          onClick={(e) => e.stopPropagation()}
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    <div
                      ref={countryListboxRef}
                      id="harbor-country-listbox"
                      role="listbox"
                      aria-labelledby="harbor-country-label"
                      className="overflow-y-auto max-h-48"
                    >
                      {filteredCountries.length > 0 ? (
                        filteredCountries.map((country, index) => (
                          <div
                            key={country.code}
                            id={`harbor-country-option-${country.code}`}
                            role="option"
                            data-index={index}
                            aria-selected={
                              formData.country_short_code === country.code
                            }
                            tabIndex={
                              countryActiveOptionIndex === index ? 0 : -1
                            }
                            className={clsx(
                              'px-4 py-2.5 cursor-pointer flex items-center gap-2 focus:outline-none focus:bg-gray-200',
                              countryActiveOptionIndex === index
                                ? 'bg-gray-200'
                                : 'hover:bg-gray-200',
                              formData.country_short_code === country.code &&
                                countryActiveOptionIndex !== index &&
                                'bg-blue-50'
                            )}
                            onClick={() => selectCountryOption(country.code)}
                          >
                            <span className="text-sm">{country.name}</span>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                          No countries found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {errors.country_short_code && (
                <p
                  id="harbor-country-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.country_short_code as string}
                </p>
              )}
            </div>
            {shouldShowBusinessEntityVatField && (
              <div>
                <label
                  htmlFor="product_vat_config_id"
                  className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
                >
                  Business Entity VAT Regime
                </label>
                <select
                  id="product_vat_config_id"
                  name="product_vat_config_id"
                  value={formData.product_vat_config_id ?? ''}
                  onChange={handleInputChange}
                  onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                  disabled={
                    isViewMode ||
                    isCreating ||
                    isUpdating ||
                    isUploadingMedia ||
                    isBusinessEntityVatConfigsLoading
                  }
                  className={clsx(
                    'w-full bg-light-active h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                    errors.product_vat_config_id
                      ? 'border-red-500 contrast:border-[#FF8B33]'
                      : 'border-gray-300 contrast:border-gray-300',
                    isViewMode || isCreating || isUpdating || isUploadingMedia
                      ? 'bg-gray-100 cursor-not-allowed'
                      : ''
                  )}
                  aria-label="Business Entity VAT Regime"
                >
                  <option value="">Select business entity VAT regime</option>
                  {businessEntityVatOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Depth and Capacity */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label
                htmlFor="min-depth-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.MIN_DEPTH' })}
              </label>
              <PriceInput
                id="min-depth-input"
                name="min_depth"
                value={formData.min_depth ?? ''}
                onChange={(value) => {
                  const nextValueForValidation =
                    value === undefined ? '' : String(value).replace('.', ',');
                  setFormData((prev) => ({
                    ...prev,
                    min_depth: value,
                  }));
                  validateField('min_depth', nextValueForValidation);
                }}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.min_depth
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.min_depth}
                aria-describedby={
                  errors.min_depth ? 'min-depth-error' : undefined
                }
                aria-label={intl.formatMessage({ id: 'COMMON.MIN_DEPTH' })}
                autoComplete="off"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_MIN_DEPTH',
                })}
              />
              {errors.min_depth && (
                <p
                  id="min-depth-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.min_depth}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="max-depth-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.MAX_DEPTH' })}
              </label>
              <PriceInput
                id="max-depth-input"
                name="max_depth"
                value={formData.max_depth ?? ''}
                onChange={(value) => {
                  const nextValueForValidation =
                    value === undefined ? '' : String(value).replace('.', ',');
                  setFormData((prev) => ({
                    ...prev,
                    max_depth: value,
                  }));
                  validateField('max_depth', nextValueForValidation);
                }}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.max_depth
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.max_depth}
                aria-describedby={
                  errors.max_depth ? 'max-depth-error' : undefined
                }
                aria-label={intl.formatMessage({ id: 'COMMON.MAX_DEPTH' })}
                autoComplete="off"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_MAX_DEPTH',
                })}
              />
              {errors.max_depth && (
                <p
                  id="max-depth-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.max_depth}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="depth-unit-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.DEPTH_UNIT' })}
              </label>
              <select
                id="depth-unit-input"
                name="depth_unit"
                value={formData.depth_unit || 'm'}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.depth_unit ? 'border-red-500' : 'border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.depth_unit}
                aria-describedby={
                  errors.depth_unit ? 'depth-unit-error' : undefined
                }
                aria-label={intl.formatMessage({ id: 'COMMON.DEPTH_UNIT' })}
              >
                <option value="m">
                  {intl.formatMessage({ id: 'COMMON.METERS' })}
                </option>
                <option value="ft">
                  {intl.formatMessage({ id: 'COMMON.FEET' })}
                </option>
              </select>
              {errors.depth_unit && (
                <p
                  id="depth-unit-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.depth_unit}
                </p>
              )}
            </div>
          </div>

          {/* Berth Capacity and VHF Channel */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="berth-capacity-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.BERTH_CAPACITY' })}
              </label>
              <NumberInput
                id="berth-capacity-input"
                name="berth_capacity"
                value={formData.berth_capacity ?? ''}
                onChange={handleInputChange}
                onFocus={handleNumberInputFocus}
                onBlur={handleNumberInputBlur}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.berth_capacity
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.berth_capacity}
                aria-describedby={
                  errors.berth_capacity ? 'berth-capacity-error' : undefined
                }
                aria-label={intl.formatMessage({ id: 'COMMON.BERTH_CAPACITY' })}
                autoComplete="off"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_BERTH_CAPACITY',
                })}
              />
              {errors.berth_capacity && (
                <p
                  id="berth-capacity-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.berth_capacity}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="vhf-channel-input"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
              >
                {intl.formatMessage({ id: 'COMMON.VHF_CHANNEL' })}
              </label>
              <input
                id="vhf-channel-input"
                type="text"
                name="vhf_channel"
                value={formData.vhf_channel ?? ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'w-full input h-11 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.vhf_channel
                    ? 'border-red-500 contrast:border-[#FF8B33]'
                    : 'border-gray-300 contrast:border-gray-300',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                )}
                aria-invalid={!!errors.vhf_channel}
                aria-describedby={
                  errors.vhf_channel ? 'vhf-channel-error' : undefined
                }
                aria-label={intl.formatMessage({ id: 'COMMON.VHF_CHANNEL' })}
                autoComplete="off"
                placeholder={intl.formatMessage({
                  id: 'COMMON.ENTER_VHF_CHANNEL',
                })}
              />
              {errors.vhf_channel && (
                <p
                  id="vhf-channel-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.vhf_channel}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="harbor-description-editor"
              className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
            >
              {intl.formatMessage({ id: 'COMMON.DESCRIPTION' })}
            </label>
            <Editor
              id="harbor-description-editor"
              tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
              value={formData.description || ''}
              init={tinymceInit}
              onEditorChange={(content: string) => {
                setFormData((prev) => ({ ...prev, description: content }));
                // Keep the current-language translation slot in sync
                setTranslations((prev) => ({
                  ...prev,
                  [currentLanguage.code]: {
                    ...prev[currentLanguage.code],
                    description: content,
                  },
                }));
              }}
              onBlur={() =>
                handleInputBlur('description', formData.description || '')
              }
              textareaName="description"
              disabled={isCreating || isUpdating || isUploadingMedia}
            />
            {errors.description && (
              <p
                id="description-error"
                className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                role="alert"
              >
                {errors.description}
              </p>
            )}
          </div>

          {/* Admin Notes - Enhanced */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="block text-sm font-medium text-gray-700 contrast:!text-white">
                {intl.formatMessage({
                  id: 'COMMON.INTERNAL_ADMINISTRATION_NOTES',
                })}
              </span>
              <span className="text-xs text-gray-500 contrast:text-white/100">
                {formData.admin_notes?.length || 0}/1000{' '}
                {intl.formatMessage({
                  id: 'COMMON.CHARACTERS',
                })}
              </span>
            </div>
            <textarea
              id="admin-notes"
              name="admin_notes"
              value={formData.admin_notes || ''}
              onChange={(e) => {
                if (e.target.value.length <= 1000) {
                  handleInputChange(e);
                }
              }}
              onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
              disabled={isCreating || isUpdating || isUploadingMedia}
              rows={4}
              maxLength={1000}
              className={clsx(
                'w-full textarea px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                errors.admin_notes ? 'border-red-500' : 'border-gray-300',
                isCreating || isUpdating || isUploadingMedia
                  ? 'bg-gray-100 cursor-not-allowed'
                  : ''
              )}
              aria-label={intl.formatMessage({
                id: 'COMMON.INTERNAL_ADMINISTRATION_NOTES',
              })}
              aria-invalid={!!errors.admin_notes}
              aria-describedby="admin-notes-helper"
              placeholder={intl.formatMessage({
                id: 'COMMON.ENTER_INTERNAL_NOTES',
              })}
            />
            <p
              id="admin-notes-helper"
              className="mt-1 text-xs text-gray-500 contrast:text-white/100"
            >
              {intl.formatMessage({ id: 'COMMON.FOR_INTERNAL_USE_ONLY' })}.{' '}
              {intl.formatMessage(
                {
                  id: 'COMMON.MAX_CHARACTERS_LIMIT',
                },
                { count: 1000 }
              )}
              .
            </p>
            {errors.admin_notes && (
              <p
                className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                role="alert"
              >
                {errors.admin_notes}
              </p>
            )}
          </div>

          {/* Facilities with Conditions */}
          {hasCapability(
            HarborCapability.DISCOVERY_MANAGE_AMENITIES_AND_SERVICES
          ) && (
            <FacilityConditionManager
              facilities={formData.facilities || []}
              facilitiesWithConditions={
                formData.facilities_with_conditions || []
              }
              onFacilitiesChange={(facilities) => {
                setFormData((prev) => ({ ...prev, facilities }));
                validateField('facilities', facilities);
              }}
              onFacilitiesWithConditionsChange={(facilitiesWithConditions) => {
                setFormData((prev) => ({
                  ...prev,
                  facilities_with_conditions: facilitiesWithConditions,
                }));
              }}
              disabled={isCreating || isUploadingMedia}
              errors={
                Array.isArray(errors.facilities)
                  ? errors.facilities[0]
                  : errors.facilities
              }
            />
          )}

          {/* Business Hours */}
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white">
              {intl.formatMessage({ id: 'COMMON.BUSINESS_HOURS' })}
            </span>
            <BusinessHours
              businessHours={formData.business_hours}
              setFormData={setFormData}
              errors={Object.fromEntries(
                Object.entries(errors).map(([key, value]) => [
                  key,
                  Array.isArray(value) ? value[0] : value,
                ])
              )}
              setErrors={(newErrors) => {
                setErrors((prev) => ({
                  ...prev,
                  ...Object.fromEntries(
                    Object.entries(newErrors).map(([key, value]) => [
                      key,
                      Array.isArray(value) ? value[0] : value,
                    ])
                  ),
                }));
              }}
              disabled={isCreating || isUploadingMedia}
            />
            {errors.business_hours && (
              <p
                id="business-hours-error"
                className="text-red-500 text-xs mt-1 contrast:!text-[#FF8B33]"
                role="alert"
              >
                {errors.business_hours}
              </p>
            )}
          </div>

          {/* Seasonality Support */}
          <div className="bg-gradient-to-r from-blue-50 dark:from-blue-950 contrast:from-black to-indigo-50 dark:to-indigo-950 contrast:to-black border border-blue-200 dark:border-blue-800 contrast:border-blue-800 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 contrast:!bg-primary/10 rounded-lg">
                  <svg
                    className="w-6 h-6 text-blue-600 "
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 ">
                    {intl.formatMessage({ id: 'HARBOR.SEASONAL_AVAILABILITY' })}
                  </h2>
                  <p className="text-sm text-gray-600 ">
                    {intl.formatMessage({
                      id: 'HARBOR.SEASONAL_AVAILABILITY_DESCRIPTION',
                    })}
                  </p>
                </div>
              </div>
              <label
                htmlFor="seasonality-support-toggle"
                className="flex items-center gap-3 cursor-pointer"
                id="seasonality-status"
              >
                <span className="text-sm font-medium text-gray-700">
                  {formData.seasonality_support
                    ? intl.formatMessage({ id: 'COMMON.ENABLED' })
                    : intl.formatMessage({ id: 'COMMON.DISABLED' })}
                </span>
                <div className="relative inline-flex items-center">
                  <input
                    id="seasonality-support-toggle"
                    type="checkbox"
                    className="sr-only peer cursor-pointer"
                    checked={formData.seasonality_support || false}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        seasonality_support: e.target.checked,
                      }))
                    }
                    disabled={isCreating || isUpdating || isUploadingMedia}
                    role="switch"
                    aria-checked={formData.seasonality_support || false}
                    aria-labelledby="seasonality-status"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 contrast:bg-gray-700 contrast:peer-checked:bg-primary"></div>
                </div>
              </label>
            </div>

            {formData.seasonality_support ? (
              <div className="space-y-4">
                {errors.seasonal_dates && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded-r-md contrast:bg-black contrast:border-[#FF8B33]">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-red-500 contrast:text-[#FF8B33]"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800 contrast:text-[#FF8B33]">
                          {intl.formatMessage({ id: 'VALIDATION.FIX_ERRORS' })}
                        </h3>
                        <div className="mt-2 text-sm text-red-700 contrast:text-[#FF8B33]">
                          <ul className="list-disc pl-5 space-y-1">
                            {typeof errors.seasonal_dates === 'string' ? (
                              <li>{errors.seasonal_dates}</li>
                            ) : Array.isArray(errors.seasonal_dates) ? (
                              errors.seasonal_dates.map((line, idx) => (
                                <li key={`seasonal-dates-error-${idx}`}>
                                  {line}
                                </li>
                              ))
                            ) : null}
                            {(() => {
                              try {
                                const seasonalDates =
                                  errors.seasonal_dates as unknown as
                                    | Record<string, Record<string, unknown>>
                                    | undefined;
                                if (!seasonalDates) return null;
                                if (typeof seasonalDates !== 'object')
                                  return null;

                                return Object.entries(seasonalDates).flatMap(
                                  ([season, seasonErrors]) => {
                                    if (
                                      !seasonErrors ||
                                      typeof seasonErrors !== 'object'
                                    )
                                      return [];

                                    return Object.entries(seasonErrors).map(
                                      ([field, error]) => {
                                        const seasonName = intl.formatMessage({
                                          id: `HARBOR.SEASON.${season.toUpperCase()}`,
                                          defaultMessage: season,
                                        });

                                        const fieldName = intl.formatMessage({
                                          id: `COMMON.${field.toUpperCase()}`,
                                          defaultMessage: field,
                                        });

                                        return (
                                          <li key={`${season}-${field}`}>
                                            {seasonName} - {fieldName}:{' '}
                                            {String(error)}
                                          </li>
                                        );
                                      }
                                    );
                                  }
                                );
                              } catch (e) {
                                console.error(
                                  'Error rendering seasonal date errors:',
                                  e
                                );
                                return null;
                              }
                            })()}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  {(['spring', 'summer', 'autumn', 'winter'] as const).map(
                    (season) => {
                      const seasonRanges =
                        formData.seasonal_dates?.[season] || [];
                      const seasonName = intl.formatMessage({
                        id: `HARBOR.SEASON.${season.toUpperCase()}`,
                      });
                      const seasonColor = {
                        spring: {
                          bg: 'from-green-50 to-green-100',
                          border:
                            'border-green-200 dark:border-green-800 contrast:border-green-800',
                          text: 'text-green-800 dark:text-green-300 contrast:text-green-300',
                          bgBadge: 'bg-green-500',
                        },
                        summer: {
                          bg: 'from-yellow-50 to-yellow-100',
                          border:
                            'border-yellow-200 dark:border-yellow-800 contrast:border-yellow-800',
                          text: 'text-yellow-800 dark:text-yellow-300 contrast:text-yellow-300',
                          bgBadge: 'bg-yellow-500',
                        },
                        autumn: {
                          bg: 'from-orange-50 to-orange-100',
                          border:
                            'border-orange-200 dark:border-orange-800 contrast:border-orange-800',
                          text: 'text-orange-800 dark:text-orange-300 contrast:text-orange-300',
                          bgBadge: 'bg-orange-500',
                        },
                        winter: {
                          bg: 'from-blue-50 to-blue-100',
                          border:
                            'border-blue-200 dark:border-blue-800 contrast:border-blue-800',
                          text: 'text-blue-800 dark:text-blue-300 contrast:text-blue-300',
                          bgBadge: 'bg-blue-500',
                        },
                      }[season];

                      return (
                        <div key={season} className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h3
                              className={`text-sm font-semibold ${seasonColor.text} `}
                            >
                              {seasonName}
                            </h3>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                const scrollContainer =
                                  (e.currentTarget.closest(
                                    '.scrollable-y-hover'
                                  ) as HTMLElement | null) ||
                                  (document.scrollingElement as HTMLElement | null);
                                const prevScrollTop =
                                  scrollContainer?.scrollTop;

                                addDateRange(season);

                                requestAnimationFrame(() => {
                                  if (
                                    scrollContainer &&
                                    typeof prevScrollTop === 'number'
                                  ) {
                                    scrollContainer.scrollTop = prevScrollTop;
                                  }
                                });
                              }}
                              disabled={
                                isCreating || isUpdating || isUploadingMedia
                              }
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed "
                            >
                              +{' '}
                              {intl.formatMessage({
                                id: 'HARBOR.DATE_RANGE.ADD',
                              })}
                            </button>
                          </div>

                          {seasonRanges.length === 0 ? (
                            <div
                              className={`p-4 rounded-lg border ${seasonColor.border} bg-gray-100 contrast:!bg-light-active`}
                            >
                              <p className="text-sm text-gray-500 contrast:!text-white">
                                {intl.formatMessage({
                                  id: 'HARBOR.NO_DATE_RANGES_ADDED',
                                })}
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {seasonRanges.map((range, index) => (
                                <div
                                  key={season + '-' + index}
                                  className={`p-4 rounded-lg border ${seasonColor.border} bg-gray-100 contrast:!bg-light-active`}
                                >
                                  <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-center gap-2">
                                      <div
                                        className={`p-1.5 ${seasonColor.bgBadge} rounded-full`}
                                      >
                                        <svg
                                          className="w-4 h-4 text-white"
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      </div>
                                      <span className="text-sm font-medium contrast:!text-white">
                                        {seasonName}{' '}
                                        {seasonRanges.length > 1
                                          ? `#${index + 1}`
                                          : ''}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center">
                                        <label
                                          htmlFor={`range-is-closed-${season}-${index}`}
                                          className="inline-flex items-center cursor-pointer"
                                        >
                                          <input
                                            id={`range-is-closed-${season}-${index}`}
                                            type="checkbox"
                                            checked={!!range.is_closed}
                                            onChange={(e) =>
                                              updateDateRange(
                                                season,
                                                index,
                                                'is_closed',
                                                e.target.checked
                                              )
                                            }
                                            className="sr-only peer cursor-pointer"
                                          />
                                          <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                                          <span className="ms-3 text-sm font-medium text-gray-700 contrast:!text-white">
                                            {range.is_closed
                                              ? intl.formatMessage({
                                                  id: 'HARBOR.DATE_RANGE.ADD_CLOSED',
                                                })
                                              : intl.formatMessage({
                                                  id: 'HARBOR.DATE_RANGE.ADD_OPEN',
                                                })}
                                          </span>
                                        </label>
                                      </div>
                                      {seasonRanges.length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            removeDateRange(season, index)
                                          }
                                          className="text-red-500 hover:text-red-700 "
                                          disabled={
                                            isCreating ||
                                            isUpdating ||
                                            isUploadingMedia
                                          }
                                          aria-label={intl.formatMessage({
                                            id: 'COMMON.REMOVE',
                                          })}
                                        >
                                          <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                            />
                                          </svg>
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label
                                        htmlFor={`start-month-${season}-${index}`}
                                        className="block text-xs font-medium text-gray-700 mb-1 contrast:!text-white"
                                      >
                                        {intl.formatMessage({
                                          id: 'COMMON.START_DATE',
                                        })}
                                      </label>
                                      <div className="flex gap-2">
                                        <select
                                          id={`start-month-${season}-${index}`}
                                          value={
                                            range.start_date?.split('-')[1] ||
                                            ''
                                          }
                                          onChange={(e) => {
                                            const [year] = (
                                              range.start_date || ''
                                            ).split('-');
                                            const newDate = `${year || CURRENT_YEAR}-${e.target.value}-${range.start_date?.split('-')[2] || '01'}`;
                                            updateDateRange(
                                              season,
                                              index,
                                              'start_date',
                                              newDate
                                            );
                                          }}
                                          disabled={
                                            isCreating ||
                                            isUpdating ||
                                            isUploadingMedia
                                          }
                                          className="flex-1 h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-light-active"
                                          aria-label={intl.formatMessage({
                                            id: 'COMMON.MONTH',
                                          })}
                                        >
                                          <option value="">
                                            {intl.formatMessage({
                                              id: 'COMMON.MONTH',
                                            })}
                                          </option>
                                          {Array.from(
                                            { length: 12 },
                                            (_, i) => i + 1
                                          ).map((monthIndex) => (
                                            <option
                                              key={monthIndex}
                                              value={String(
                                                monthIndex
                                              ).padStart(2, '0')}
                                            >
                                              {getTranslatedMonthName(
                                                monthIndex
                                              )}
                                            </option>
                                          ))}
                                        </select>
                                        {/* <label
                                          htmlFor={`start-day-${season}-${index}`}
                                          className="sr-only block text-xs font-medium text-gray-700 mb-1 contrast:!text-white"
                                        >
                                          {intl.formatMessage({
                                            id: 'COMMON.DAY',
                                          })}
                                        </label> */}
                                        <select
                                          id={`start-day-${season}-${index}`}
                                          value={
                                            range.start_date?.split('-')[2] ||
                                            ''
                                          }
                                          onChange={(e) => {
                                            const [year, month] = (
                                              range.start_date || ''
                                            ).split('-');
                                            const newDate = `${year || CURRENT_YEAR}-${month || '01'}-${e.target.value}`;
                                            updateDateRange(
                                              season,
                                              index,
                                              'start_date',
                                              newDate
                                            );
                                          }}
                                          disabled={
                                            isCreating ||
                                            isUpdating ||
                                            isUploadingMedia
                                          }
                                          className="flex-1 h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-light-active"
                                          aria-label={intl.formatMessage({
                                            id: 'COMMON.DAY',
                                          })}
                                        >
                                          <option value="">
                                            {intl.formatMessage({
                                              id: 'COMMON.DAY',
                                            })}
                                          </option>
                                          {getAvailableDays(
                                            range.start_date?.split('-')[1] ||
                                              ''
                                          ).map((day) => (
                                            <option
                                              key={day}
                                              value={String(day).padStart(
                                                2,
                                                '0'
                                              )}
                                            >
                                              {day}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                    <div>
                                      <label
                                        htmlFor={`end-month-${season}-${index}`}
                                        className="block text-xs font-medium text-gray-700 mb-1 contrast:!text-white"
                                      >
                                        {intl.formatMessage({
                                          id: 'COMMON.END_DATE',
                                        })}
                                      </label>
                                      <div className="flex gap-2">
                                        <select
                                          id={`end-month-${season}-${index}`}
                                          value={
                                            range.end_date?.split('-')[1] || ''
                                          }
                                          onChange={(e) => {
                                            const [year] = (
                                              range.end_date || ''
                                            ).split('-');
                                            const newDate = `${year || CURRENT_YEAR}-${e.target.value}-${range.end_date?.split('-')[2] || '01'}`;
                                            updateDateRange(
                                              season,
                                              index,
                                              'end_date',
                                              newDate
                                            );
                                          }}
                                          disabled={
                                            isCreating ||
                                            isUpdating ||
                                            isUploadingMedia
                                          }
                                          className="flex-1 h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-light-active"
                                          aria-label={intl.formatMessage({
                                            id: 'COMMON.MONTH',
                                          })}
                                        >
                                          <option value="">
                                            {intl.formatMessage({
                                              id: 'COMMON.MONTH',
                                            })}
                                          </option>
                                          {Array.from(
                                            { length: 12 },
                                            (_, i) => i + 1
                                          ).map((monthIndex) => (
                                            <option
                                              key={monthIndex}
                                              value={String(
                                                monthIndex
                                              ).padStart(2, '0')}
                                            >
                                              {getTranslatedMonthName(
                                                monthIndex
                                              )}
                                            </option>
                                          ))}
                                        </select>
                                        <select
                                          id={`end-day-${season}-${index}`}
                                          value={
                                            range.end_date?.split('-')[2] || ''
                                          }
                                          onChange={(e) => {
                                            const [year, month] = (
                                              range.end_date || ''
                                            ).split('-');
                                            const newDate = `${year || CURRENT_YEAR}-${month || '01'}-${e.target.value}`;
                                            updateDateRange(
                                              season,
                                              index,
                                              'end_date',
                                              newDate
                                            );
                                          }}
                                          disabled={
                                            isCreating ||
                                            isUpdating ||
                                            isUploadingMedia
                                          }
                                          className="flex-1 h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-light-active"
                                          aria-label={intl.formatMessage({
                                            id: 'COMMON.DAY',
                                          })}
                                        >
                                          <option value="">
                                            {intl.formatMessage({
                                              id: 'COMMON.DAY',
                                            })}
                                          </option>
                                          {getAvailableDays(
                                            range.end_date?.split('-')[1] || ''
                                          ).map((day) => (
                                            <option
                                              key={day}
                                              value={String(day).padStart(
                                                2,
                                                '0'
                                              )}
                                            >
                                              {day}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-700 ">
                {intl.formatMessage({ id: 'HARBOR.SEASONALITY_DISABLED' })}
              </div>
            )}
          </div>

          {/* Media Upload */}
          {hasCapability(HarborCapability.DISCOVERY_MANAGE_PHOTOS) && (
            <div>
              <MediaUpload
                field="media"
                media={formData.media}
                media_captions={formData.media_captions}
                setFormData={setFormData}
                errors={Object.fromEntries(
                  Object.entries(errors).map(([key, value]) => [
                    key,
                    Array.isArray(value) ? value[0] : value,
                  ])
                )}
                setErrors={(newErrors) => {
                  setErrors((prev) => ({
                    ...prev,
                    ...Object.fromEntries(
                      Object.entries(newErrors).map(([key, value]) => [
                        key,
                        Array.isArray(value) ? value[0] : value,
                      ])
                    ),
                  }));
                }}
                disabled={isCreating || isUpdating || isUploadingMedia}
                allowedTypes={[
                  'image/jpeg',
                  'image/jpg',
                  'image/png',
                  'image/svg+xml',
                  'video/mp4',
                ]}
                maxFiles={5}
                previewMode={true}
              />
              {errors.media && (
                <p
                  id="media-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.media}
                </p>
              )}
            </div>
          )}

          {/* Preview Message */}
          {showPreviewMessage && (mode === 'create' || mode === 'edit') && (
            <div className="mt-6 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 border border-blue-200 dark:border-blue-800 contrast:border-blue-800 rounded-lg p-4 flex items-center justify-between gap-2">
              <div className="flex items-center">
                <KeenIcon
                  type="outline"
                  icon="paper-clip"
                  className="w-5 h-5 text-blue-600 dark:text-blue-100 contrast:text-blue-100 mr-2"
                />
                <p className="text-blue-700 dark:text-blue-100 contrast:text-blue-100 text-sm">
                  {intl.formatMessage({
                    id: 'COMMON.TRANSLATION_PREVIEW_AVAILABLE',
                  })}
                </p>
              </div>
              <button
                type="button"
                onClick={handleOpenPreview}
                className="btn btn-lg bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Show preview"
              >
                {intl.formatMessage({ id: 'COMMON.SHOW_PREVIEW' })}
              </button>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end gap-4 pb-4">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isCreating || isUpdating || isUploadingMedia}
              className={clsx(
                'btn btn-sm btn-light flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-gray-700',
                isCreating || isUpdating || isUploadingMedia
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500'
              )}
              aria-label={
                mode === 'create'
                  ? 'Cancel harbor creation'
                  : 'Cancel harbor editing'
              }
            >
              {intl.formatMessage({ id: 'COMMON.CANCEL' })}
            </button>
            {(mode === 'create' || harborData?.harbor_status === 1) && (
              <button
                type="button"
                onClick={() => handleSubmit(true)}
                disabled={isCreating || isUpdating || isUploadingMedia}
                className={clsx(
                  'btn btn-sm btn-outline-primary flex items-center gap-2 rounded-lg px-4 py-2 text-blue-600 border-blue-600 dark:text-blue-400 dark:border-blue-400 contrast:text-white',
                  isCreating || isUpdating || isUploadingMedia
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-blue-50 dark:hover:bg-blue-800 contrast:hover:bg-blue-500 dark:hover:text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500'
                )}
                aria-label={
                  mode === 'create'
                    ? 'Save harbor as draft'
                    : 'Update harbor as draft'
                }
              >
                {isCreating ||
                  isUpdating ||
                  (isUploadingMedia && (
                    <span className="animate-spin">
                      <KeenIcon type="filled" icon="spinner" />
                    </span>
                  ))}
                {intl.formatMessage({ id: 'COMMON.SAVE_AS_DRAFT' })}
              </button>
            )}
            <button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={isCreating || isUpdating || isUploadingMedia}
              className={clsx(
                'btn btn-sm btn-primary flex items-center gap-2 rounded-lg px-4 py-2 text-white',
                isCreating || isUpdating || isUploadingMedia
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500'
              )}
            >
              {isCreating || isUpdating || isUploadingMedia ? (
                <span className="animate-spin">
                  <KeenIcon type="filled" icon="spinner" aria-hidden="true" />
                </span>
              ) : (
                <KeenIcon type="filled" icon="check" aria-hidden="true" />
              )}
              {mode === 'create' || harborData?.harbor_status === 1
                ? intl.formatMessage({ id: 'COMMON.PUBLISH' })
                : intl.formatMessage({ id: 'COMMON.UPDATE' })}
            </button>
          </div>
        </div>
      </div>
      <Tooltip id="tooltip" />

      {/* Harbor Preview Modal */}
      <HarborPreviewModal
        open={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        formData={formData}
        source_language={intl.locale}
        initialTranslations={translations}
        onTranslationsGenerated={handleTranslationsGenerated}
      />
    </Fragment>
  );
};

export { HarborForm };
